import Box from '@/Atoms/Box';
import Card from '@/Atoms/Card';
import React, {useCallback, useEffect, useState, useMemo, useRef} from 'react';
import {styles} from './styles';
import uuid from 'react-native-uuid';
import {Chess} from 'chess.js';
import CCText from '@/Atoms/CCText';
import {useGetStudyGamesDetail} from '@/Hooks/GetStudyGamesDetailsApi/useGetStudyGamesDetail';
import {useGetChapterMoves} from '@/Hooks/GetStudyGamesDetailsApi/useGetChapterMoves';
import {useNavigation} from '@react-navigation/native';
import {useRoute} from '@react-navigation/native';
import {
  ScrollView,
  Switch,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import WorkbookMovesList from '@/Atoms/WorkbookMovesList';
import MoveUpdateBar from '@/Atoms/WorkbookMovesList/MoveUpdateBar';
import Spacer from '@/Atoms/Spacer';
import CCLoader from '@/Atoms/CCLoader';
import {ChessConfirm, CheckeredStrip} from '@/Components/ChessModal';
import Ionicons from 'react-native-vector-icons/Ionicons';
import {
  STARTING_FEN,
  cleanPgn,
  generateMoveData,
  getStartingFenFromPgn,
  getUserDetails,
  getLocalStorage,
  setLocalStorage,
} from '@/Utils/CommonUtils';
import useOpeningExplorer, {
  DEFAULT_EXPLORER_CONFIG,
  ExplorerConfig,
  ExplorerDb,
  ExplorerGame,
} from '@/Hooks/OpeningExplorer/useOpeningExplorer';
import useTablebase from '@/Hooks/OpeningExplorer/useTablebase';
import useExplorerGamePgn from '@/Hooks/OpeningExplorer/useExplorerGamePgn';
import GameViewerTabs from './Component/OpeningExplorer/GameViewerTabs';
import GameMovesList from './Component/OpeningExplorer/GameMovesList';
import TablebasePanel from './Component/OpeningExplorer/TablebasePanel';
import {countPiecesInFen, TABLEBASE_MAX_PIECES} from './utils/explorerUtils';
import {ScreenName} from '@/navigators/StackConstants';
import CCTabs from '@/Atoms/CCTabs';
import Participants from './Component/Participants';
import Chapters from './Component/Chapters';
import CevalPanel from '@/Components/CevalPanel';
import useUpdateMoveShapes from '@/Hooks/StudyAlternateMoves/useUpdateMoveShapes';
import {FontSizes, WINDOW_HEIGHT, WINDOW_WIDTH} from '@/Utils/Dimensions';
import useStudySaveAlternateMoves from '@/Hooks/StudyAlternateMoves/useStudySaveAlternateMoves';
import useStudyUpdateAnnotation from '@/Hooks/StudyUpdateAnnotation/useStudyUpdateAnnotation';
import {useGetChapterProgress} from '@/Hooks/StudyChapterProgress/useGetChapterProgress';
import useSaveChapterProgress from '@/Hooks/StudyChapterProgress/useSaveChapterProgress';
import useSound from 'use-sound';
import practiceSuccessSound from '@/Assets/Sounds/success.mp3';
import useMoveTreeUndo from '@/Hooks/ManageMoves/useMoveTreeUndo';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import BottomSheet from '@/Atoms/BottomSheet';
import {TabView} from 'react-native-tab-view';
import {renderTabBarPill} from '@/Components/TabBar/TabBarPill';
import ConditionalRender from '@/Atoms/ConditionalRender';
import {MOBILE_HEADER_HEIGHT} from '@/Utils/Constants';
import useChessboardColorTheme from '@/Hooks/ChessboardColorTheme/useChessboardColorTheme';
import ChessboardWithArrow from '@/Components/ChessboardWithArrow';
import {useNotificationStore} from '@/Store/useNotificationStore';
import OpeningExplorer from './Component/OpeningExplorer';
import {searchMoveTree} from './utils/searchMoves';
import {
  getNextMoveOptions,
  isBadMove,
  isOnMainline,
  getMainlineBranchPoint,
} from './utils/lessonMoves';
import {flattenMoveTree} from './utils/moveTreeBranchOps';
import {
  addMoveToTree,
  isSamePosition,
  patchMoveInTree,
  promoteBranchLocally,
  replaceNodeId,
} from './utils/moveTreeOps';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {useToast} from '@/toast/useToast';
import {LessonMoveRole} from './types';
import HelpModal from './Component/HelpModal';
import PracticePanel from './Component/PracticeStatusCards/PracticePanel';
import PlayVsComputerCard from './Component/PracticeStatusCards/PlayVsComputerCard';
import ExerciseResultsCard from './Component/PracticeStatusCards/ExerciseResultsCard';
import useGetChapterProgressRoster from '@/Hooks/StudyChapterProgress/useGetChapterProgressRoster';
import {
  applyStrengthPolicy,
  difficultyByKey,
  difficultyFromElo,
  type PracticeStrengthPolicy,
} from './constants/practiceDifficulty';
import LessonFeedbackCard from './Component/LessonFeedbackCard';
import WorkbookSettingsTab from './Component/WorkbookSettingsTab';
import {useStudySettings} from './hooks/useStudySettings';
import {useEngineAnalysis, CEVAL_MAX_DEPTH} from './hooks/useEngineAnalysis';
import {usePracticeGame} from './hooks/usePracticeGame';
import {useChapterNavigation} from './hooks/useChapterNavigation';
import EvalBar from './Component/EvalBar';
import CurrentMoveCard from './Component/CurrentMoveCard';
import {
  NavArrows,
  BoardNavigationToolbar,
  BoardToolbarActions,
  OpeningChip,
  WorkbookHeaderTabs,
} from './Component/BoardToolbar';
import {
  AnnotationsPopover,
  LessonPopover,
} from './Component/LessonAuthoringPopover';
import {sanitizeFen, isDatabaseId} from './utils/studyHelpers';
import type {PracticeOutcome} from './utils/practiceOutcome';

function Study() {
  const {isMobileOrTab} = useMediaQuery();
  const route = useRoute();

  const gameId = route?.params?.study_id;
  const {
    data: studyDetails,
    isLoading,
    refetch,
  } = useGetStudyGamesDetail(gameId);

  const [chessboardHeight, setChessboardHeight] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isResetHovered, setIsResetHovered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [movesList, setMovesList] = useState({});
  const [tabSelected, setTabSelected] = useState(0);
  const [parentHeight, setParentHeight] = useState(0);
  const [parentWidth, setParentWidth] = useState(0);
  const [boardWidth, setBoardWidth] = useState(0);
  const [, setCenterWidth] = useState(0);
  const [, setCenterHeight] = useState(0);
  const navigation = useNavigation();
  // Callers (Chapters delete, Participants remove) may also pass optional
  // ChessConfirm theming: title, piece, pieceRotation, confirmLabel, bullets.
  const [openRemoveConfimation, setOpenRemoveConfimation] = useState<any>({
    flag: false,
    bodyTxt: '',
    callbackFunc: () => {},
  });
  const [moveSelected, setmoveSelected] = useState({
    move: '',
    id: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
    fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
    lastFen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
  });
  const [annotationToShow, setAnnotationToShow] = useState('');
  const [userDetailsData, setUserDetailsData] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [previewAsParticipant, setPreviewAsParticipant] = useState(false);
  // Lets the owner see the chapter exactly as a non-owner participant would
  // (practice engine, lesson feedback, hidden moves), without needing a
  // second account. Only the owner can toggle this; management/edit
  // affordances (chapter settings, delete, participants) stay keyed off the
  // real isOwner so the owner can always switch preview back off.
  const effectiveIsOwner = isOwner && !previewAsParticipant;
  const pendingSavesRef = useRef<Record<string, Promise<any>>>({});

  const [evalResult, setEvalResult] = useState<any>(null);
  // Opening-explorer database + filters, persisted per user (see the
  // load/save effects next to the explorer hook below).
  const [explorerConfig, setExplorerConfig] = useState<ExplorerConfig>(
    DEFAULT_EXPLORER_CONFIG,
  );
  const [moveSearchQuery, setMoveSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [lessonFeedback, setLessonFeedback] = useState<{
    status: 'success' | 'error' | 'idle' | 'branch-end';
    message: string;
  }>({
    status: 'idle',
    message: '',
  });
  // Lichess's gamebook reveals the authored hint only on demand (a "Get a
  // hint" button that's hidden entirely if no hint was authored), rather
  // than surfacing it automatically as the wrong-move message. `hasWrongAttempt`
  // additionally gates the "View the solution" escape hatch, which Lichess
  // only shows once the student is stuck (`myTurn` after at least one miss).
  const [lessonHintRevealed, setLessonHintRevealed] = useState(false);
  const [lessonHasWrongAttempt, setLessonHasWrongAttempt] = useState(false);
  // "Analyse this line" on the practice end card dismisses the win/fail
  // card so the student can keep exploring the finished position freely
  // (moves are already ungated in practice mode) without the verdict UI
  // in the way. Reset whenever the chapter changes.
  const [dismissedPracticeEndCard, setDismissedPracticeEndCard] =
    useState(false);
  // Everything else about a practice attempt — its own move list, move
  // counter, verdict, outcome and the computer's replies — lives in
  // usePracticeGame (wired up below, after the engine hook it consumes).

  const [participantsList, setParticipantsList] = useState([]);
  const [chapterList, setChapterList] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  // `conceal_moves` is an independent toggle from `chapter_mode` (so it can
  // combine with Analysis/Lesson/Practice). `chapter_mode === 'hide_moves'`
  // is kept as a read-only back-compat alias for chapters saved before this
  // split - see useUpdateChapterSettings/Chapters settings modal, which no
  // longer ever writes that value going forward. Declared this early (rather
  // than down near isLessonMode) so the nav handlers below (handleNextMove/
  // handleLastMove/autoplay) can gate on it too - conceal must stop a
  // student from skipping past a move they haven't actually played, not
  // just hide the move *list*.
  const isConcealMovesOn =
    Boolean(selectedChapter?.conceal_moves) ||
    selectedChapter?.chapter_mode === 'hide_moves';
  // Lesson/Practice chapters replace the move list with their own guided
  // flow (validated moves / engine play) - Next/Last/autoplay would let a
  // student skip straight to a position they haven't earned there too, so
  // block forward nav for students in those modes exactly like conceal.
  const isGuidedStudyMode =
    selectedChapter?.chapter_mode === 'lesson' ||
    selectedChapter?.chapter_mode === 'practice';
  const [index, setIndex] = React.useState(0);
  const [newChapterCreated, setNewChapterCreated] = useState(false);
  const [boardOrientation, setBoardOrientation] = useState(
    selectedChapter?.orientation === 'W' ? 'white' : 'black',
  );
  const [playerOrChaptersSelect, setPlayerOrChaptersSelect] = useState(0);

  const [routes] = React.useState(
    [
      {key: 'PARTICIPANTS', title: 'Participants'},
      {key: 'CHAPTERS', title: 'Chapters'},
    ]?.filter(i => i),
  );

  const {mutate: updateMoveShapes} = useUpdateMoveShapes();
  const {
    data: chapterMovesResponse,
    isLoading: isChapterMovesLoading,
    refetch: refetchChapterMoves,
  } = useGetChapterMoves(selectedChapter?.id);

  const {mutateAsync: saveAlternateMoves, isLoading: isSavingMoves} =
    useStudySaveAlternateMoves();

  const {mutateAsync: saveAnnotation, isLoading: isSavingAnnotation} =
    useStudyUpdateAnnotation();

  // Beyond Lichess parity: Lichess itself doesn't persist granular gamebook
  // progress either, but it's worth it here so a student can resume an
  // Interactive Lesson chapter where they left off (see StudyChapterProgress
  // on the backend). Scoped to lesson mode only, not practice/analysis.
  const isLessonProgressMode =
    selectedChapter?.chapter_mode === 'lesson' && !effectiveIsOwner;
  // Practice chapters don't resume a position (see practiceCompletedRef
  // below), but we still need their completion flag to know when to lift
  // the engine/explorer/analysis-tab restrictions further down, so fetch
  // progress for both guided modes, not just lesson.
  const isGuidedProgressMode = isGuidedStudyMode && !effectiveIsOwner;
  const {data: chapterProgressResponse, refetch: refetchChapterProgress} =
    useGetChapterProgress(selectedChapter?.id, isGuidedProgressMode);
  // Students get engine assist / opening explorer / the Analysis tab only
  // once they've solved the chapter themselves - otherwise it's too easy to
  // just look up the answer instead of working it out. Owners previewing
  // their own study are never restricted.
  // Backend returns completed_at (a timestamp, or null), not a plain
  // `completed` boolean - see get_chapter_progress in study_game.py.
  const isChapterCompleted = Boolean(
    chapterProgressResponse?.data?.completed_at,
  );
  const isChapterLocked = isGuidedProgressMode && !isChapterCompleted;
  // The Tools *tab* itself unlocks earlier than its contents do, for practice
  // chapters only. The Sandbox card lives in that tab and its whole job on an
  // unsolved Exercise is to say "finish or reset your attempt first" - which a
  // disabled tab made permanently unreachable, so the student just saw a dead
  // control with no explanation. Everything inside the tab (engine, explorer,
  // tablebase) is still gated on isChapterLocked exactly as before, and lesson
  // chapters keep the tab locked outright since nothing there addresses them.
  const isToolsTabLocked =
    isChapterLocked && selectedChapter?.chapter_mode !== 'practice';
  // Lesson/Practice chapters replace the move list with their own guided
  // flow (validated moves / engine play) - Next/Last/autoplay would let a
  // student skip straight to a position they haven't earned there too, so
  // block forward nav for students in those modes exactly like conceal.
  // Once the chapter is completed, the guided-mode restriction lifts (same
  // trigger as unlocking the Analysis tab) so the student can freely review
  // and explore both sides of the position; conceal_moves stays independent
  // of completion since it's a pure display setting, not a guided-mode gate.
  const isForwardNavBlocked =
    (isConcealMovesOn || (isGuidedStudyMode && !isChapterCompleted)) &&
    !effectiveIsOwner;
  const {mutate: saveChapterProgress} = useSaveChapterProgress();
  const resumedProgressChapterIdRef = useRef<number | string | null>(null);
  const [playPracticeSuccessSound] = useSound(practiceSuccessSound);

  // Declared up here (rather than beside the practice UI further down)
  // because the engine hook, the eval-result handler and usePracticeGame all
  // need them, and those are all wired before the render section.

  const chessboardColorTheme = useChessboardColorTheme();

  const showToast = useToast();

  const currentChapterMoves = selectedChapter?.id
    ? movesList[selectedChapter.id]
    : undefined;
  const currentChapterFen = useMemo(() => {
    if (!selectedChapter) {
      return 'start';
    }
    const detailedData = chapterMovesResponse?.data;
    if (detailedData && detailedData.chapter_id === selectedChapter.id) {
      if (detailedData.pgn) {
        try {
          const fenFromPgn = getStartingFenFromPgn(cleanPgn(detailedData.pgn));
          if (fenFromPgn) {
            return fenFromPgn;
          }
        } catch (e) {}
      }
      if (detailedData.fen && detailedData.fen !== 'start') {
        return detailedData.fen;
      }
    }
    return selectedChapter.fen || 'start';
  }, [selectedChapter, chapterMovesResponse]);

  /* ─── Play vs Computer ────────────────────────────────────────────────
   * Two ways in, one engine underneath (see usePracticeGame):
   *
   *  - EXERCISE: the chapter is `chapter_mode === 'practice'`. A coach set the
   *    position, side, goal and strength, so it auto-arms on open, it's
   *    assessed, and a win records completion.
   *  - SANDBOX: anyone toggles it on from the Tools tab, from whatever
   *    position is on the board, choosing their own side and strength. Never
   *    assessed, never recorded, available to the owner without preview mode.
   *
   * `practiceSession` is whichever one is currently armed. Sandbox wins when
   * both could apply, and it's deliberately session-local: no persistence, it
   * clears on reload and on chapter switch. */
  const isExerciseChapter =
    selectedChapter?.chapter_mode === 'practice' && !effectiveIsOwner;
  // Which colour the student plays. `practice_player_color` is the coach's
  // explicit choice; `orientation` is the fallback for chapters authored
  // before that setting existed, where the two were necessarily the same
  // answer. Keeping the fallback is what makes this backward compatible.
  const chapterStudentColor: 'w' | 'b' =
    selectedChapter?.practice_player_color === 'w' ||
    selectedChapter?.practice_player_color === 'b'
      ? selectedChapter.practice_player_color
      : selectedChapter?.orientation === 'B'
      ? 'b'
      : 'w';
  // Matches Lichess's practiceGoal chapter tag (mate/mateIn/equalize/
  // advantage/promotion) - see StudyChapter.practice_goal on the backend.
  // Null means no explicit goal, in which case practice falls back to the
  // "reached the end of the authored line" condition.
  const chapterPracticeGoal = selectedChapter?.practice_goal as
    | {type: string; value?: number}
    | null
    | undefined;
  // Opponent strength. The chapter stores a target Elo (null = full
  // strength, which is what every chapter saved before this field existed
  // reads back as); the viewer may pick their own for themselves without
  // touching the chapter.
  const chapterDifficulty = difficultyFromElo(
    selectedChapter?.practice_difficulty as number | null | undefined,
  );
  const [studentDifficultyKey, setStudentDifficultyKey] = useState<
    string | null
  >(null);

  const [sandboxSession, setSandboxSession] = useState<{
    startFen: string;
    studentColor: 'w' | 'b';
    goal: {type: string; value?: number} | null;
    difficultyKey: string;
    /** Bumped on re-arm so the same position twice is still a fresh game. */
    nonce: number;
  } | null>(null);
  // Where the board was when the Sandbox was armed, so exiting puts the
  // viewer back where they were instead of stranding them on the last move of
  // a game they were only experimenting with.
  const sandboxReturnRef = useRef<any>(null);

  const isSandboxActive = Boolean(sandboxSession);
  const isPracticeMode = isSandboxActive || isExerciseChapter;

  const practiceStudentColor: 'w' | 'b' =
    sandboxSession?.studentColor ?? chapterStudentColor;
  const practiceGoal = sandboxSession
    ? sandboxSession.goal
    : chapterPracticeGoal;
  const chosenPracticeDifficulty = sandboxSession
    ? difficultyByKey(sandboxSession.difficultyKey)
    : studentDifficultyKey
    ? difficultyByKey(studentDifficultyKey)
    : chapterDifficulty;
  // A Sandbox is nobody's assessment, and the owner is never assessed, so
  // the coach's strength policy binds the Exercise path only. Clamping here
  // (not just in the picker) is what actually decides the engine's Elo.
  const practiceDifficulty =
    isSandboxActive || !isExerciseChapter
      ? chosenPracticeDifficulty
      : applyStrengthPolicy(
          chosenPracticeDifficulty,
          (selectedChapter?.practice_strength_policy ??
            null) as PracticeStrengthPolicy,
          selectedChapter?.practice_difficulty as number | null | undefined,
        );
  // Where an Exercise is played from. The coach may pin any position in the
  // chapter's tree (`practice_start_fen`); with none pinned it is the
  // chapter's own start, which is what every chapter authored before that
  // setting existed does. Validated on the way in as well as on the way out:
  // a stored FEN that no longer parses must not take the board down.
  const chapterPracticeStartFen = useMemo(() => {
    const stored = selectedChapter?.practice_start_fen as string | null;
    if (!stored) {
      return currentChapterFen;
    }
    try {
      const probe = new Chess(stored);
      return probe.isGameOver() ? currentChapterFen : stored;
    } catch (e) {
      return currentChapterFen;
    }
  }, [selectedChapter?.practice_start_fen, currentChapterFen]);

  const practiceStartFen = sandboxSession?.startFen ?? chapterPracticeStartFen;
  // The start position and the student's colour are part of the game's
  // identity, not just its configuration: a coach editing either mid-session
  // has to abandon the half-played attempt rather than let it carry on from a
  // position the exercise no longer starts at.
  const practiceSessionKey = sandboxSession
    ? `sandbox:${selectedChapter?.id ?? 'x'}:${sandboxSession.nonce}`
    : `chapter:${selectedChapter?.id ?? 'x'}:${sanitizeFen(
        chapterPracticeStartFen,
      )}:${chapterStudentColor}`;

  /**
   * The authored line an Exercise is measured against — the mainline
   * CONTINUATION from wherever the exercise starts, not the whole chapter.
   *
   * With a pinned start position the two differ: the moves before the pinned
   * node have already happened, so comparing the student's play against them
   * would never match. Falls back to the whole mainline when nothing is
   * pinned (every pre-existing chapter), and to no reference line at all when
   * the pinned position isn't on the mainline — better no fallback win
   * condition than one measured against the wrong moves.
   */
  const exerciseAuthoredLine = useMemo(() => {
    const tree = movesList?.[selectedChapter?.id as any] as any[] | undefined;
    if (!tree?.length) {
      return undefined;
    }
    const pinned = selectedChapter?.practice_start_fen as string | null;
    if (!pinned) {
      return tree;
    }
    const target = sanitizeFen(chapterPracticeStartFen);
    if (target === sanitizeFen(currentChapterFen)) {
      return tree;
    }
    const idx = tree.findIndex(
      node => node?.fen && sanitizeFen(node.fen) === target,
    );
    return idx === -1 ? undefined : tree.slice(idx + 1);
  }, [
    movesList,
    selectedChapter?.id,
    selectedChapter?.practice_start_fen,
    chapterPracticeStartFen,
    currentChapterFen,
  ]);

  // Engine assistance during a practice game: the eval bar, the best-move
  // arrows and the engine's principal-variation lines. All three point at the
  // move you're being asked to find, so they're off by default for the
  // duration of a game - but it's the viewer's call, not a rule, so this is a
  // toggle rather than a hard block. Off is merely the honest default.
  //
  // Deliberately NOT persisted: it resets with every new game (see the effect
  // just after usePracticeGame below), so turning it on to check one position
  // can't silently leave assistance on for the next attempt or exercise.
  const [practiceAssistEnabled, setPracticeAssistEnabled] = useState(false);
  // Bumped to force the board to throw away its internal chess.js state and
  // reload from `position`. The board only watches its position/lastMoveFen
  // strings, so a rejected move — where the position is unchanged by
  // definition — needs this to snap back.
  const [boardResyncNonce, setBoardResyncNonce] = useState(0);
  /** True when a game is running and the viewer hasn't opted back in. */
  const isAssistSuppressed = isPracticeMode && !practiceAssistEnabled;
  const assistSuppressedReason = isAssistSuppressed
    ? 'Hidden while you’re playing this position.'
    : undefined;

  // A viewer's own difficulty choice, and any armed Sandbox, are per-chapter -
  // switching chapters drops both rather than carrying a half-played game into
  // an unrelated position.
  useEffect(() => {
    setStudentDifficultyKey(null);
    setSandboxSession(null);
    sandboxReturnRef.current = null;
  }, [selectedChapter?.id]);

  const setRefetchStudy = useNotificationStore(state => state.setRefetchStudy);
  const refetchStudyFlag = useNotificationStore(state => state.refetchStudy);

  const onChessboardLayout = event => {
    const {height} = event.nativeEvent.layout;
    setChessboardHeight(height);
  };

  const handleFlipBoard = () => {
    setBoardOrientation(prev => (prev === 'white' ? 'black' : 'white'));
  };

  const studySettings = useStudySettings({
    gameId,
    studyDetails,
    selectedChapter,
    moveSelected,
    refetch,
    setLoading,
    showToast,
  });

  const onParentLayout = (event: any) => {
    const {width, height} = event.nativeEvent.layout;
    setParentWidth(width);
    setParentHeight(height);
  };

  const onCenterLayout = (event: any) => {
    const {width, height} = event.nativeEvent.layout;
    setCenterWidth(width);
    setCenterHeight(height);
  };

  const handleShapesChange = useCallback(
    async shapes => {
      if (!moveSelected?.id) {
        return;
      }
      const chapterId = selectedChapter?.id;
      if (!chapterId) {
        return;
      }

      setmoveSelected(prev => ({...prev, shapes}));

      setMovesList(prevMovesList => {
        const updateMoveInTree = movesArr =>
          movesArr.map(move => {
            if (move.id === moveSelected.id) {
              return {
                ...move,
                shapes,
              };
            }
            if (move.child_moves && move.child_moves.length > 0) {
              return {
                ...move,
                child_moves: updateMoveInTree(move.child_moves),
              };
            }
            return move;
          });

        return {
          ...prevMovesList,
          [chapterId]: updateMoveInTree(prevMovesList[chapterId] || []),
        };
      });

      let resolvedMoveId = moveSelected.id;
      if (
        resolvedMoveId &&
        typeof resolvedMoveId === 'string' &&
        resolvedMoveId.includes('_')
      ) {
        const pendingPromise = pendingSavesRef.current[resolvedMoveId];
        if (pendingPromise) {
          try {
            const saved = await pendingPromise;
            resolvedMoveId = saved?.move_id || resolvedMoveId;
          } catch (err) {
            console.error(
              'Failed to resolve pending move save for shapes:',
              err,
            );
            showToast('Failed to save drawing', {type: 'Error'});
          }
        }
      }

      if (!isDatabaseId(resolvedMoveId)) {
        return;
      }

      updateMoveShapes(
        {
          move_id: resolvedMoveId,
          shapes,
        },
        {
          onError: err => {
            console.error('Failed to save shapes:', err);
            showToast('Failed to save drawing', {type: 'Error'});
          },
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [moveSelected?.id, selectedChapter?.id, showToast],
  );

  // Practice's own verdict is cleared by usePracticeGame (on reset/takeback),
  // not from here - this is the lesson-mode feedback state only.
  const resetLessonUiState = useCallback(() => {
    setLessonFeedback({status: 'idle', message: ''});
    setLessonHintRevealed(false);
    setLessonHasWrongAttempt(false);
    setDismissedPracticeEndCard(false);
  }, []);

  // Matches Lichess's gamebook end card ("Next chapter" button, space bar
  // to advance - see gamebookPlayCtrl's 'end' feedback state) and the
  // equivalent affordance in Practice mode.
  const hasNextChapter = Boolean(
    selectedChapter &&
      chapterList?.length &&
      chapterList.findIndex((c: any) => c?.id === selectedChapter.id) !== -1 &&
      chapterList.findIndex((c: any) => c?.id === selectedChapter.id) + 1 <
        chapterList.length,
  );

  const handleGoToNextChapter = useCallback(() => {
    if (!chapterList?.length || !selectedChapter) {
      return;
    }
    const idx = chapterList.findIndex((c: any) => c?.id === selectedChapter.id);
    if (idx === -1 || idx + 1 >= chapterList.length) {
      return;
    }
    const next = chapterList[idx + 1];
    setSelectedChapter(next);
    setBoardOrientation(next?.orientation === 'W' ? 'white' : 'black');
    setNewChapterCreated(false);
    resetLessonUiState();
  }, [chapterList, selectedChapter, resetLessonUiState]);

  // Study-level keyboard shortcuts, matching lichess's study keymap: f
  // (flip board), n (next chapter), g (open the glyph/annotation tool), ?
  // (toggle this shortcuts help panel). Move navigation (←/→/↑/↓/Shift+←/→)
  // lives in WorkbookMovesList, which already owns the move tree. Guarded
  // against firing while typing anywhere on the page, same as that listener.
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  // Which tab the "?" help modal opens on - Shortcuts (keyboard reference)
  // or Info (feature guide: right-click menus, where chapters/participants
  // live, undo windows, etc.). See renderHelpModal.
  const [helpModalTab, setHelpModalTab] = useState<'shortcuts' | 'info'>(
    'info',
  );
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const eventTarget = event.target as HTMLElement | null;
      if (eventTarget && ['INPUT', 'TEXTAREA'].includes(eventTarget.tagName)) {
        return;
      }
      if (event.key === '?') {
        setShowShortcutsHelp(prev => !prev);
      } else if (event.key === 'f' || event.key === 'F') {
        handleFlipBoard();
      } else if (event.key === 'n' || event.key === 'N') {
        handleGoToNextChapter();
      } else if (event.key === 'g' || event.key === 'G') {
        setActiveToolbarTool('annotations');
      } else if (event.key === 'Escape') {
        setShowShortcutsHelp(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleGoToNextChapter]);

  // Jumping to the chapter start must also abandon the current practice
  // attempt outright - otherwise the attempt's moves would survive a Reset
  // and the next attempt would continue the old game. usePracticeGame is
  // created further down (it consumes the engine hook), so the navigation
  // hook reaches its reset through this ref.
  const practiceResetRef = useRef<(() => void) | null>(null);
  const handleNavResetToStart = useCallback(() => {
    practiceResetRef.current?.();
  }, []);

  const {
    selectMoveFromList,
    handleFirstMove,
    handlePrevMove,
    handleNextMove,
    handleLastMove,
    isAutoplayOn,
    setIsAutoplayOn,
    autoplaySpeed,
    setAutoplaySpeed,
    showAutoplayMenu,
    setShowAutoplayMenu,
  } = useChapterNavigation({
    selectedChapter,
    chapterStartFen: currentChapterFen,
    movesList,
    moveSelected,
    setmoveSelected,
    isForwardNavBlocked,
    resetLessonUiState,
    onResetToStart: handleNavResetToStart,
  });

  // Opening-explorer "top games" viewer: clicking a game opens it as a
  // browser-style tab above the board instead of leaving the app. Only the
  // active tab's PGN is fetched/replayed; ply -1 means the game's start
  // position, 0..n-1 means the position right after that ply. Declared
  // before analysisFen below, which needs activeGameFen to make the
  // engine/explorer tools track the game's current position while one is
  // open (see analysisFen's own comment).
  const [gameTabs, setGameTabs] = useState<
    Array<{
      id: string;
      label: string;
      db: ExplorerDb;
      plyIndex: number;
      /** Full game record (players/rating/result/year) - kept alongside the
       * tab so the workspace's move-list panel can show a header without
       * re-fetching, since the explorer's top-games response already has it. */
      meta: ExplorerGame;
    }>
  >([]);
  const [activeGameTabId, setActiveGameTabId] = useState<string | null>(null);
  const activeGameTab = gameTabs.find(t => t.id === activeGameTabId) || null;
  const activeGamePgnQuery = useExplorerGamePgn(
    activeGameTab?.id,
    activeGameTab?.db || 'masters',
    !!activeGameTab,
  );
  const activeGameTotalPlies = activeGamePgnQuery.data?.plies.length || 0;
  const activeGameFen = activeGameTab
    ? activeGameTab.plyIndex === -1
      ? activeGamePgnQuery.data?.startFen
      : activeGamePgnQuery.data?.plies[activeGameTab.plyIndex]?.fen
    : undefined;
  const activeGameLastMoveFen = activeGameTab
    ? activeGameTab.plyIndex === -1
      ? undefined
      : activeGameTab.plyIndex === 0
      ? activeGamePgnQuery.data?.startFen
      : activeGamePgnQuery.data?.plies[activeGameTab.plyIndex - 1]?.fen
    : undefined;

  const shortPlayerName = (name: string) => (name || '?').split(',')[0].trim();

  // Opening explorer + endgame tablebase (Lichess data, cached per
  // position). At 7 men or fewer the Analysis tab swaps the explorer for
  // the tablebase panel, exactly as lichess does. Prefers the open game's
  // current position over the chapter's own when a game tab is active, so
  // the engine/explorer/tablebase tools analyse what's actually on the
  // board instead of a stale chapter position hidden behind it.
  const analysisFen =
    (activeGameTab ? activeGameFen : undefined) ||
    moveSelected?.fen ||
    selectedChapter?.fen ||
    STARTING_FEN;
  const analysisPieceCount = countPiecesInFen(analysisFen);
  const isTablebasePosition = analysisPieceCount <= TABLEBASE_MAX_PIECES;
  // Stopped (not fetched) while a lesson/practice chapter is still locked -
  // see isChapterLocked - so students can't sidestep solving it themselves
  // by looking the position up in the database.
  const explorerQuery = useOpeningExplorer(
    analysisFen,
    explorerConfig,
    !isTablebasePosition && !isChapterLocked,
  );
  const tablebaseQuery = useTablebase(analysisFen, !isChapterLocked);
  const openingName = explorerQuery.data?.opening?.name || '';

  const handleOpenGameTab = useCallback(
    (game: ExplorerGame) => {
      setGameTabs(prev => {
        if (prev.some(t => t.id === game.id)) {
          return prev;
        }
        return [
          ...prev,
          {
            id: game.id,
            label: `${shortPlayerName(game.white.name)} - ${shortPlayerName(
              game.black.name,
            )}`,
            db: explorerConfig.db,
            plyIndex: -1,
            meta: game,
          },
        ];
      });
      setActiveGameTabId(game.id);
    },
    [explorerConfig.db],
  );

  const handleCloseGameTab = useCallback(
    (id: string) => {
      setGameTabs(prev => {
        const next = prev.filter(t => t.id !== id);
        if (id === activeGameTabId) {
          setActiveGameTabId(next.length ? next[next.length - 1].id : null);
        }
        return next;
      });
    },
    [activeGameTabId],
  );

  const handleCloseAllGameTabs = useCallback(() => {
    setGameTabs([]);
    setActiveGameTabId(null);
  }, []);

  const stepActiveGame = useCallback(
    (delta: number) => {
      if (!activeGameTabId) {
        return;
      }
      setGameTabs(prev =>
        prev.map(t =>
          t.id === activeGameTabId
            ? {
                ...t,
                plyIndex: Math.max(
                  -1,
                  Math.min(activeGameTotalPlies - 1, t.plyIndex + delta),
                ),
              }
            : t,
        ),
      );
    },
    [activeGameTabId, activeGameTotalPlies],
  );

  // Jumps straight to a given ply (as opposed to stepActiveGame's +/-1) -
  // used by the workspace's move-list panel, where clicking any move should
  // jump the board there directly instead of stepping through every ply
  // in between.
  const jumpToGamePly = useCallback(
    (plyIndex: number) => {
      if (!activeGameTabId) {
        return;
      }
      const clamped = Math.max(
        -1,
        Math.min(activeGameTotalPlies - 1, plyIndex),
      );
      setGameTabs(prev =>
        prev.map(t =>
          t.id === activeGameTabId ? {...t, plyIndex: clamped} : t,
        ),
      );
    },
    [activeGameTabId, activeGameTotalPlies],
  );

  // Restore the coach's last explorer setup once, then persist changes.
  const explorerConfigLoadedRef = useRef(false);
  useEffect(() => {
    (async () => {
      const stored = (await getLocalStorage(
        'study:explorer-config',
      )) as ExplorerConfig | null;
      if (stored?.db && stored?.speeds?.length && stored?.ratings?.length) {
        setExplorerConfig(stored);
      }
      explorerConfigLoadedRef.current = true;
    })();
  }, []);
  useEffect(() => {
    if (explorerConfigLoadedRef.current) {
      setLocalStorage('study:explorer-config', explorerConfig);
    }
  }, [explorerConfig]);

  const findExistingMoveBySan = useCallback(
    (san: string) => {
      const mainMoves = movesList[selectedChapter?.id as any] || [];
      if (!moveSelected || moveSelected.id === 'start') {
        return mainMoves.find((m: any) => m.san === san) || null;
      }
      const mainIndex = mainMoves.findIndex(
        (m: any) => m.id === moveSelected.id,
      );
      if (mainIndex !== -1) {
        const mainlineNext = mainMoves[mainIndex + 1];
        if (mainlineNext && mainlineNext.san === san) {
          return mainlineNext;
        }
        return (
          (moveSelected.child_moves || []).find((m: any) => m.san === san) ||
          null
        );
      }
      return (
        (moveSelected.child_moves || []).find((m: any) => m.san === san) || null
      );
    },
    [movesList, selectedChapter?.id, moveSelected],
  );

  // Explorer/tablebase row click: navigate if the move is already in the
  // tree; in a normal-analysis chapter a move that isn't yet in the tree is
  // played onto the board (routing through handlePositionChange, which adds
  // it as a mainline extension or branch) — same as clicking a move in
  // lichess's explorer. Lesson/practice chapters stay navigate-only so the
  // authored line can't be sidestepped from the database panel.
  const handleExplorerMoveClick = useCallback(
    (san: string) => {
      const existing = findExistingMoveBySan(san);
      if (existing) {
        setmoveSelected(existing);
        return;
      }
      const chapterMode = selectedChapter?.chapter_mode;
      if (chapterMode && chapterMode !== 'analysis') {
        return;
      }
      const fromFen = moveSelected?.fen || selectedChapter?.fen || STARTING_FEN;
      try {
        const chess = new Chess(sanitizeFen(fromFen));
        const played = chess.move(san);
        if (played) {
          handlePositionChange({
            newFen: chess.fen(),
            lastFen: fromFen,
            san: played.san,
          });
        }
      } catch (e) {
        console.warn('Failed to play explorer move:', san, e);
      }
    },
    // handlePositionChange is declared later in the component (plain const,
    // re-created every render) - it's safe to call from this closure but
    // can't be listed as a dep without hitting its TDZ during render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      findExistingMoveBySan,
      selectedChapter?.chapter_mode,
      selectedChapter?.fen,
      moveSelected?.fen,
    ],
  );

  const highlightedMoveIds = useMemo(
    () =>
      searchMoveTree(movesList?.[selectedChapter?.id as any], moveSearchQuery),
    [movesList, selectedChapter?.id, moveSearchQuery],
  );

  // `targetMoveId` defaults to the selected move; the draft-flush on move
  // switch passes the *previous* move's id explicitly, since by the time it
  // runs the selection has already advanced.
  const handleSaveLessonInfo = async (
    hint: string,
    successMessage: string,
    deviationMessage: string,
    targetMoveId?: string | number,
  ) => {
    const moveId = targetMoveId ?? moveSelected?.id;
    if (!moveId) {
      return;
    }
    const chapterId = selectedChapter?.id;
    if (!chapterId) {
      return;
    }

    // Apply local state updates first
    if (moveId === moveSelected?.id) {
      setmoveSelected(prev => ({
        ...prev,
        hint: hint,
        success_message: successMessage,
        deviation_message: deviationMessage,
      }));
    }

    setMovesList(prevMovesList => ({
      ...prevMovesList,
      [chapterId]: patchMoveInTree(prevMovesList[chapterId] || [], moveId, {
        hint,
        success_message: successMessage,
        deviation_message: deviationMessage,
      }),
    }));

    let resolvedMoveId = moveId;
    if (
      resolvedMoveId &&
      typeof resolvedMoveId === 'string' &&
      resolvedMoveId.includes('_')
    ) {
      const pendingPromise = pendingSavesRef.current[resolvedMoveId];
      if (pendingPromise) {
        try {
          const saved = await pendingPromise;
          resolvedMoveId = saved?.move_id || resolvedMoveId;
        } catch (err) {
          console.error(
            'Failed to resolve pending move save for lesson info:',
            err,
          );
          showToast('Failed to save lesson details', {type: 'Error'});
        }
      }
    }

    if (!isDatabaseId(resolvedMoveId)) {
      return;
    }

    try {
      await saveAnnotation({
        move_id: resolvedMoveId,
        hint: hint,
        success_message: successMessage,
        deviation_message: deviationMessage,
      });
    } catch (error) {
      console.warn('Failed to save lesson details:', error);
      showToast('Failed to save lesson details', {type: 'Error'});
      return;
    }
  };

  // usePracticeGame is created *after* the engine hook (it consumes the
  // engine's output), but the engine's result handler has to feed it — so the
  // handler reaches the practice hook through this ref rather than the two
  // hooks depending on each other directly.
  const practiceIngestRef = useRef<
    ((result: any, searchedFen: string) => void) | null
  >(null);

  const handleStockfishResult = useCallback(
    (result: any, searchedFen?: string) => {
      if (!result) {
        setEvalResult(null);
        return;
      }
      // Tag the eval with the FEN the ENGINE actually searched, not with
      // whatever position is selected right now. The two differ in the window
      // between a move landing and the search restarting — precisely when the
      // practice verdict and the goal checks run — so reading the selection
      // here used to mislabel a pre-move eval as belonging to the post-move
      // position. Falls back to the selection only for the engine layer's
      // pre-searchedFen contract.
      const analyzedFen =
        searchedFen ||
        moveSelected?.fen ||
        selectedChapter?.fen ||
        STARTING_FEN;
      setEvalResult({...result, fen: analyzedFen});
      practiceIngestRef.current?.(result, analyzedFen);
    },

    [selectedChapter?.fen, moveSelected?.fen],
  );

  const {
    enableStockfish,
    setEnableStockfish,
    cevalFen,
    threatFen,
    cevalPvs,
    cevalDepth,
    cevalMultiPv,
    setCevalMultiPv,
    showBestMoveArrow,
    setShowBestMoveArrow,
    effectiveThreatMode,
    toggleThreatMode,
    handleCevalToggle,
    setHoveredPvUci,
    nextBestMoveUci,
    opponentBestMoveUci,
    engineAutoShapes,
    cevalEngineElement,
    threatEngineElement,
    opponentEngineElement,
  } = useEngineAnalysis({
    analysisFen,
    chapterId: selectedChapter?.id,
    isChapterLocked,
    practiceMode: isPracticeMode,
    assistSuppressed: isAssistSuppressed,
    practiceElo: practiceDifficulty.elo,
    practiceDepth: practiceDifficulty.depth,
    practiceMovetime: practiceDifficulty.movetime,
    practiceStudentColor,
    onResult: handleStockfishResult,
  });

  // A viewer who already solved this Exercise in an earlier session should
  // keep seeing it as solved (with Restart still on offer) rather than a
  // blank "ongoing" attempt on every revisit - best_result (not `result`,
  // which is only the *latest* attempt) is exactly "has this ever been
  // won", so a later failed retry can't un-complete it. Sandbox and the
  // owner's own preview have no notion of "completed" here.
  const previousPracticeOutcome = useMemo<PracticeOutcome | null>(() => {
    if (isSandboxActive || !isExerciseChapter || effectiveIsOwner) {
      return null;
    }
    const progress = chapterProgressResponse?.data;
    if (progress?.best_result !== 'won') {
      return null;
    }
    const moveCount = progress?.move_count;
    return {
      status: 'won',
      reason:
        'You already completed this exercise' +
        (moveCount ? ` in ${moveCount} move${moveCount === 1 ? '' : 's'}` : '') +
        '.',
    };
  }, [
    isSandboxActive,
    isExerciseChapter,
    effectiveIsOwner,
    chapterProgressResponse,
  ]);

  const practice = usePracticeGame({
    isActive: isPracticeMode,
    sessionKey: practiceSessionKey,
    startFen: practiceStartFen,
    studentColor: practiceStudentColor,
    practiceGoal,
    // The "reached the end of the authored line" fallback only makes sense
    // for an Exercise, which starts where the authored line starts. A Sandbox
    // armed from an arbitrary position has no authored line to run out of.
    authoredTree: isSandboxActive ? undefined : exerciseAuthoredLine,
    moveSelected,
    setmoveSelected,
    evalResult,
    nextBestMoveUci,
    opponentBestMoveUci,
    engineEnabled: enableStockfish,
    // Coach policies bind the assessed Exercise only — a Sandbox is the
    // viewer's own game, and the owner is never assessed. Enforced in the hook
    // rather than only by disabling the panel button.
    takebacksAllowed:
      isSandboxActive || !isExerciseChapter
        ? null
        : (selectedChapter?.practice_takebacks_allowed as number | null) ??
          null,
    initialOutcome: previousPracticeOutcome,
  });

  // Engine assistance is per-game, and "game" means the attempt, not just the
  // session: sessionKey alone changes on a chapter switch or a Sandbox re-arm
  // but NOT on Restart, so a viewer who turned assistance on to check one
  // position would silently keep it for every retry after. attemptNonce is
  // what actually increments on each fresh attempt.
  useEffect(() => {
    setPracticeAssistEnabled(false);
  }, [practiceSessionKey, practice.attemptNonce]);

  useEffect(() => {
    practiceIngestRef.current = practice.ingestEngineResult;
  }, [practice.ingestEngineResult]);

  // Arm the Sandbox from whatever position the board is showing right now -
  // mainline, deep inside a variation, doesn't matter. The current selection
  // is stashed so exiting returns the viewer to it rather than leaving them
  // on the last move of a game they were only experimenting with.
  const handleStartSandbox = useCallback(
    (opts: {
      studentColor: 'w' | 'b';
      difficultyKey: string;
      goal: {type: string; value?: number} | null;
    }) => {
      const fromFen =
        moveSelected?.fen || selectedChapter?.fen || currentChapterFen;
      sandboxReturnRef.current = moveSelected;
      setEnableStockfish(true);
      setSandboxSession(prev => ({
        startFen: sanitizeFen(fromFen),
        studentColor: opts.studentColor,
        goal: opts.goal,
        difficultyKey: opts.difficultyKey,
        nonce: (prev?.nonce ?? 0) + 1,
      }));
    },

    [moveSelected, selectedChapter?.fen, currentChapterFen, setEnableStockfish],
  );

  const handleExitSandbox = useCallback(() => {
    setSandboxSession(null);
    const back = sandboxReturnRef.current;
    sandboxReturnRef.current = null;
    setmoveSelected(
      back || {
        move: '',
        id: 'start',
        fen: currentChapterFen,
        lastFen: currentChapterFen,
      },
    );
  }, [currentChapterFen]);

  useEffect(() => {
    practiceResetRef.current = isPracticeMode ? practice.reset : null;
  }, [isPracticeMode, practice.reset]);

  // `annotationText`/`targetMoveId` default to the current comment box and
  // selected move, but accept explicit overrides so a move switch can flush
  // a still-debouncing edit for the move being switched *away from* (see
  // the annotationDraftRef flush below) instead of losing it.
  const handleSaveAnnotation = async (
    annotationText?: string,
    targetMoveId?: string | number,
  ) => {
    const moveId = targetMoveId ?? moveSelected?.id;
    if (!moveId || moveId === 'start') {
      return;
    }
    const text = annotationText ?? annotationToShow;
    const chapterId = selectedChapter?.id;

    let resolvedMoveId = moveId;
    if (typeof resolvedMoveId === 'string' && resolvedMoveId.includes('_')) {
      const pendingPromise = pendingSavesRef.current[resolvedMoveId];
      if (pendingPromise) {
        try {
          const saved = await pendingPromise;
          resolvedMoveId = saved?.move_id || resolvedMoveId;
        } catch (err) {
          console.error(
            'Failed to resolve pending move save for annotation:',
            err,
          );
          showToast('Failed to save annotation', {type: 'Error'});
        }
      }
    }

    // Apply local state updates first so comments/annotations show up in the UI immediately
    if (moveId === moveSelected?.id) {
      setmoveSelected(prev => ({...prev, annotation: text}));
    }
    setMovesList(prevMovesList => ({
      ...prevMovesList,
      [chapterId]: patchMoveInTree(prevMovesList[chapterId] || [], moveId, {
        annotation: text,
      }),
    }));

    // Skip network synchronization if we are not on a persisted database move
    if (!isDatabaseId(resolvedMoveId)) {
      return;
    }

    try {
      await saveAnnotation({
        move_id: resolvedMoveId,
        annotation: text,
      });
    } catch (error) {
      console.warn('Failed to save annotation:', (error as any)?.message);
      showToast('Failed to save annotation', {type: 'Error'});
      return;
    }
  };

  // `targetMoveId` defaults to the selected move; the move list's
  // mark-as-role context menu passes an explicit id so a branch can be
  // re-classified without selecting it first.
  const handleSaveAnnotationIcon = async (
    annotationIconSelected: string,
    targetMoveId?: string | number,
  ) => {
    const moveId = targetMoveId ?? moveSelected?.id;
    if (!moveId || moveId === 'start') {
      return;
    }
    const isTargetSelected = moveId === moveSelected?.id;
    const chapterId = selectedChapter?.id;

    let resolvedMoveId = moveId;
    if (
      resolvedMoveId &&
      typeof resolvedMoveId === 'string' &&
      resolvedMoveId.includes('_')
    ) {
      const pendingPromise = pendingSavesRef.current[resolvedMoveId];
      if (pendingPromise) {
        try {
          const saved = await pendingPromise;
          resolvedMoveId = saved?.move_id || resolvedMoveId;
        } catch (err) {
          console.error(
            'Failed to resolve pending move save for annotation icon:',
            err,
          );
          showToast('Failed to save annotation', {type: 'Error'});
        }
      }
    }

    // Apply local state updates first
    if (isTargetSelected) {
      setmoveSelected(prev => ({
        ...prev,
        annotation_icon: annotationIconSelected,
      }));
    }

    // Update the movelist
    setMovesList(prevMovesList => ({
      ...prevMovesList,
      [chapterId]: patchMoveInTree(prevMovesList[chapterId] || [], moveId, {
        // The comment mirror only belongs to the selected move's editor
        // state — patching it onto an arbitrary target would clobber that
        // move's own comment.
        ...(isTargetSelected ? {annotation: annotationToShow} : {}),
        annotation_icon: annotationIconSelected,
      }),
    }));

    // Skip network synchronization if we are not on a persisted database move
    if (!isDatabaseId(resolvedMoveId)) {
      return;
    }

    try {
      await saveAnnotation({
        move_id: resolvedMoveId,
        annotation_icon: annotationIconSelected,
      });
    } catch (error) {
      console.warn('Failed to save annotation icon:', (error as any)?.message);
      showToast('Failed to save annotation', {type: 'Error'});
      return;
    }
  };

  // Latest typed-but-unsaved comment, so switching moves flushes it instead
  // of the debounce timer's cleanup silently discarding it - same pattern
  // as lessonDraftRef below, for the same reason.
  const annotationDraftRef = useRef<{
    moveId: string | number | null;
    annotation: string;
    dirty: boolean;
  }>({moveId: null, annotation: '', dirty: false});

  useEffect(() => {
    const draft = annotationDraftRef.current;
    if (
      draft.dirty &&
      draft.moveId != null &&
      draft.moveId !== moveSelected?.id
    ) {
      draft.dirty = false;
      handleSaveAnnotation(draft.annotation, draft.moveId);
    }
    setAnnotationToShow(moveSelected?.annotation || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moveSelected?.id]);

  // Mobile-only now (see renderToolIconsRow) - just which of its icons
  // reads as "active" for highlighting. Desktop's annotations/lesson tools
  // used to share this to pick which panel showed in the pencil flyout;
  // they're now two independent slide-up popovers instead (see
  // showAnnotationsPopover/showLessonPopover below).
  const [activeToolbarTool, setActiveToolbarTool] = useState<
    'annotations' | 'lesson'
  >('annotations');

  // Desktop-only "Stage + Workspace" chrome: chapters live behind a
  // breadcrumb flyout instead of a permanent sidebar, and the workspace
  // column is a single tabbed panel (Moves/Analysis/Settings) instead of
  // everything stacked at once. Mobile is unaffected - it has its own
  // bottom-sheet tab layout via `leftComponent`/`tabDetails` below.
  const [showChapterFlyout, setShowChapterFlyout] = useState(false);
  // Participants used to share showChapterFlyout/playerOrChaptersSelect
  // (a tab inside the same dropdown as Chapters). It's now its own flyout,
  // anchored under the participant-count pill instead of the breadcrumb.
  const [showParticipantsFlyout, setShowParticipantsFlyout] = useState(false);
  const [isChapterPillHovered, setIsChapterPillHovered] = useState(false);
  const [isParticipantPillHovered, setIsParticipantPillHovered] =
    useState(false);
  // Same "entered" a-frame-after-mount pattern as the Annotations/Lesson
  // popovers below (see their own comment) - was a hard cut in/out, now a
  // quick fade + slide down so it reads as one consistent popover language
  // across the page instead of these two being the odd ones out.
  const [chapterFlyoutEntered, setChapterFlyoutEntered] = useState(false);
  useEffect(() => {
    if (!showChapterFlyout) {
      setChapterFlyoutEntered(false);
      return undefined;
    }
    const raf = requestAnimationFrame(() => setChapterFlyoutEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [showChapterFlyout]);
  const [participantsFlyoutEntered, setParticipantsFlyoutEntered] =
    useState(false);
  useEffect(() => {
    if (!showParticipantsFlyout) {
      setParticipantsFlyoutEntered(false);
      return undefined;
    }
    const raf = requestAnimationFrame(() => setParticipantsFlyoutEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [showParticipantsFlyout]);

  // Annotations/Lesson each slide up from their own board-toolbar icon
  // (see renderBoardToolbarActions) instead of sharing one pencil-icon
  // flyout - so each gets its own visibility flag plus an "entered" flag
  // that flips a frame after mount, to animate the slide-up/fade-in.
  const [showAnnotationsPopover, setShowAnnotationsPopover] = useState(false);
  const [annotationsPopoverEntered, setAnnotationsPopoverEntered] =
    useState(false);
  useEffect(() => {
    if (!showAnnotationsPopover) {
      setAnnotationsPopoverEntered(false);
      return undefined;
    }
    const raf = requestAnimationFrame(() => setAnnotationsPopoverEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [showAnnotationsPopover]);

  const [showLessonPopover, setShowLessonPopover] = useState(false);
  const [lessonPopoverEntered, setLessonPopoverEntered] = useState(false);
  useEffect(() => {
    if (!showLessonPopover) {
      setLessonPopoverEntered(false);
      return undefined;
    }
    const raf = requestAnimationFrame(() => setLessonPopoverEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [showLessonPopover]);
  // The desktop workspace is a single "Workbook" panel (was two columns
  // artificially labeled "Chapter"/"Worktree" that didn't actually describe
  // what lived in them) with three tabs: Moves, Analysis (engine + opening
  // explorer, since both are just "what does the position say"), and
  // Settings (owner-only chapter config).
  const [workbookTab, setWorkbookTab] = useState<
    'moves' | 'analysis' | 'settings'
  >('moves');
  // Bounce back to Moves if a chapter switch (or a progress refetch) lands
  // us on the disabled Analysis tab while it's locked - mirrors the
  // force-enable-engine effect below for the inverse case.
  useEffect(() => {
    // Keyed to isToolsTabLocked, not isChapterLocked: a practice chapter keeps
    // the tab reachable so the Sandbox card can explain why it's unavailable
    // (the contents stay gated). Bouncing on the stricter flag would undo that
    // by kicking the student straight back to Moves.
    if (isToolsTabLocked && workbookTab === 'analysis') {
      setWorkbookTab('moves');
    }
  }, [isToolsTabLocked, workbookTab]);
  // Settings is owner-only, and its tab body renders nothing at all for a
  // non-owner. Switching preview-as-participant on while sitting on Settings
  // therefore left the whole workspace column blank with no way to tell what
  // had happened - bounce to Moves the same way the locked-Analysis case
  // above does.
  useEffect(() => {
    if (!effectiveIsOwner && workbookTab === 'settings') {
      setWorkbookTab('moves');
    }
  }, [effectiveIsOwner, workbookTab]);
  // The Settings tab is itself split into two sub-tabs (Chapter config vs.
  // workbook-level Share & Export) instead of stacking both in one long
  // scroll - see renderSettingsTab.

  useEffect(() => {
    setShowChapterFlyout(false);
  }, [selectedChapter?.id]);

  const [lessonHint, setLessonHint] = useState('');
  const [lessonSuccessMessage, setLessonSuccessMessage] = useState('');
  const [lessonDeviationMessage, setLessonDeviationMessage] = useState('');
  // Which of the Perfect-move guidance fields (Success/Hint/Wrong-move) is
  // showing in renderLessonPopover - they're tabbed rather than stacked, so
  // only one TextInput is visible/focusable at a time.
  const [lessonPerfectFieldTab, setLessonPerfectFieldTab] = useState<
    'success' | 'hint' | 'deviation'
  >('success');
  const [lessonAutoSaveStatus, setLessonAutoSaveStatus] = useState<
    'idle' | 'saving' | 'saved'
  >('idle');
  // Latest typed-but-unsaved lesson fields, so a move switch can flush them
  // instead of the debounce timer's cleanup silently discarding them (the
  // 800ms window below is plenty of time to type a hint and click the next
  // move).
  const lessonDraftRef = useRef<{
    moveId: string | number | null;
    hint: string;
    successMessage: string;
    deviationMessage: string;
    dirty: boolean;
  }>({
    moveId: null,
    hint: '',
    successMessage: '',
    deviationMessage: '',
    dirty: false,
  });

  useEffect(() => {
    const draft = lessonDraftRef.current;
    if (
      draft.dirty &&
      draft.moveId != null &&
      draft.moveId !== moveSelected?.id
    ) {
      draft.dirty = false;
      handleSaveLessonInfo(
        draft.hint,
        draft.successMessage,
        draft.deviationMessage,
        draft.moveId,
      );
    }
    setLessonHint(moveSelected?.hint || '');
    setLessonSuccessMessage(moveSelected?.success_message || '');
    setLessonDeviationMessage(moveSelected?.deviation_message || '');
    setLessonAutoSaveStatus('idle');
    setLessonPerfectFieldTab('success');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moveSelected?.id]);

  // Auto-saves as the coach types, throttled, matching lila's gamebookEdit
  // (content auto-saves via throttled socket communication rather than a
  // manual Save button). Harmless no-op re-save when merely switching
  // between already-authored moves (values already match what's stored).
  useEffect(() => {
    // effectiveIsOwner (not raw isOwner) - "Preview as Participant" must
    // never trigger background saves while walking through moves in the
    // student view.
    if (!effectiveIsOwner || !moveSelected?.id || moveSelected.id === 'start') {
      return undefined;
    }
    lessonDraftRef.current = {
      moveId: moveSelected.id,
      hint: lessonHint,
      successMessage: lessonSuccessMessage,
      deviationMessage: lessonDeviationMessage,
      dirty: true,
    };
    setLessonAutoSaveStatus('saving');
    const timer = setTimeout(async () => {
      lessonDraftRef.current.dirty = false;
      await handleSaveLessonInfo(
        lessonHint,
        lessonSuccessMessage,
        lessonDeviationMessage,
      );
      setLessonAutoSaveStatus('saved');
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonHint, lessonSuccessMessage, lessonDeviationMessage]);

  // Auto-saves the plain comment as the coach types, throttled - same
  // debounced flow as the lesson fields above, rather than a manual Save
  // button (see annotationDraftRef above for the move-switch flush).
  useEffect(() => {
    if (!effectiveIsOwner || !moveSelected?.id || moveSelected.id === 'start') {
      return undefined;
    }
    annotationDraftRef.current = {
      moveId: moveSelected.id,
      annotation: annotationToShow,
      dirty: true,
    };
    setLessonAutoSaveStatus('saving');
    const timer = setTimeout(async () => {
      annotationDraftRef.current.dirty = false;
      await handleSaveAnnotation();
      setLessonAutoSaveStatus('saved');
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotationToShow]);

  const saveMove = async ({
    tempId,
    parent_id,
    san,
    fen,
    chapter_id,
  }: {
    tempId: string;
    parent_id: string;
    san: string;
    fen: string;
    chapter_id: any;
  }) => {
    // Recording a new move means the tree just moved on — finalize whatever
    // promote/delete undo was still pending rather than let it linger against
    // a now-stale context.
    moveUndo.flush();

    // 1. Resolve parent_id if it's 'start' or falsy
    let resolvedParentId =
      parent_id === 'start' || !parent_id ? null : parent_id;

    // 2. If parent_id is a temporary ID, wait for its parent save to complete
    if (
      resolvedParentId &&
      typeof resolvedParentId === 'string' &&
      resolvedParentId.includes('_')
    ) {
      const parentPromise = pendingSavesRef.current[resolvedParentId];
      if (parentPromise) {
        try {
          const parentSaved = await parentPromise;
          resolvedParentId = parentSaved?.move_id || null;
        } catch (err) {
          console.error('Failed to resolve parent move save:', err);
          showToast('Failed to save move', {type: 'Error'});
          resolvedParentId = null;
        }
      } else {
        // If there's no pending promise, parent save might have failed or not started
        resolvedParentId = null;
      }
    }

    // 3. Perform the save mutation and store the promise in the pending ref
    const savePromise = saveAlternateMoves({
      parent_id: resolvedParentId,
      san,
      fen,
      chapter_id,
    });

    pendingSavesRef.current[tempId] = savePromise;

    try {
      const savedMoveId = await savePromise;
      return savedMoveId;
    } catch (error) {
      console.error('Error saving move:', error);
      showToast('Failed to save move', {type: 'Error'});
      return null;
    }
  };

  const refetchStudy = useCallback(async () => {
    setLoading(true);
    await Promise.allSettled([refetch(), refetchChapterMoves()]);
    setLoading(false);
  }, [refetch, refetchChapterMoves]);

  // Promote flips the branch into the mainline locally the instant it's
  // clicked (and back on Undo) — the refetch reconciles with the server.
  const applyLocalPromote = useCallback(
    (moveId: string | number) => {
      const chapterId = selectedChapter?.id;
      if (!chapterId) {
        return;
      }
      setMovesList(prev => ({
        ...prev,
        [chapterId]: promoteBranchLocally(prev[chapterId] || [], moveId),
      }));
    },
    [selectedChapter?.id],
  );

  const moveUndo = useMoveTreeUndo(
    selectedChapter?.id,
    refetchStudy,
    applyLocalPromote,
  );

  useEffect(() => {
    if (refetchStudyFlag) {
      refetchStudy();
      setRefetchStudy(false);
    }
  }, [refetchStudyFlag, refetchStudy, setRefetchStudy]);

  // Returns the *live* node matching targetMove (by id, falling back to fen
  // if it has no id), not just whether one exists - a tree mutation like
  // promote/undo can leave a node's id in place while its parent/priority/
  // child_moves/annotation fields change underneath the stale moveSelected
  // object, so "is it still there" isn't enough; callers need the fresh
  // node to actually resync to.
  const findMoveInTree = (moves, targetMove) => {
    if (!targetMove || !moves?.length) {
      return null;
    }
    for (const move of moves) {
      if (
        (targetMove?.id && move?.id === targetMove.id) ||
        (!targetMove?.id && move?.fen === targetMove?.fen)
      ) {
        return move;
      }
      const found = findMoveInTree(move?.child_moves || [], targetMove);
      if (found) {
        return found;
      }
    }
    return null;
  };

  useEffect(() => {
    const getInitialMove = (moves: any[]) => {
      const fallback = {
        move: '',
        fen:
          selectedChapter?.fen ||
          'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
        lastFen:
          selectedChapter?.fen ||
          'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
        id: 'start',
      };
      if (selectedChapter?.chapter_mode === 'lesson' && !effectiveIsOwner) {
        const startingFen =
          selectedChapter?.fen ||
          'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
        const startingTurn = startingFen.split(' ')[1] || 'w';
        const studentColor = selectedChapter?.orientation === 'B' ? 'b' : 'w';
        if (startingTurn === studentColor) {
          return fallback;
        }
      }
      return moves?.[0] || fallback;
    };
    const firstMove = getInitialMove(currentChapterMoves || []);
    setmoveSelected(firstMove as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChapter?.id]);

  useEffect(() => {
    const getInitialMove = (moves: any[]) => {
      const fallback = {
        move: '',
        fen:
          selectedChapter?.fen ||
          'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
        lastFen:
          selectedChapter?.fen ||
          'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
        id: 'start',
      };
      if (selectedChapter?.chapter_mode === 'lesson' && !effectiveIsOwner) {
        const startingFen =
          selectedChapter?.fen ||
          'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
        const startingTurn = startingFen.split(' ')[1] || 'w';
        const studentColor = selectedChapter?.orientation === 'B' ? 'b' : 'w';
        if (startingTurn === studentColor) {
          return fallback;
        }
      }
      return moves?.[0] || fallback;
    };
    const firstMove = getInitialMove(currentChapterMoves || []);
    const freshMove = findMoveInTree(currentChapterMoves || [], moveSelected);
    if (!freshMove) {
      setmoveSelected(firstMove as any);
    } else if (freshMove !== moveSelected) {
      // The id (or fen) still resolves, but a promote/undo (or any other
      // tree mutation) can leave this exact node's own fields - parent,
      // priority, child_moves, annotation/hint/success/deviation - stale on
      // the old moveSelected object even though it's "still in the tree".
      // Re-sync to the live node instead of trusting the old snapshot;
      // keep whatever transient, non-tree fields moveSelected carries
      // (move/lastFen/newFen) that selection assigned on top of the node.
      setmoveSelected((prev: any) => ({...prev, ...freshMove}));
    }
    // Depend on the array reference (a new one on every refetch — see
    // setMovesList's updater above) rather than just `.length`: a delete or
    // promote inside a variation doesn't change the top-level mainline count,
    // but it can still invalidate the currently-selected move.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChapter?.fen, currentChapterMoves]);

  const updateUserData = async gameData => {
    let userDetails = await getUserDetails();
    userDetails = JSON.parse(userDetails);
    // return userDetails?.mobile_number === data?.data?.owner?.mobile_number;
    setIsOwner(
      userDetails?.mobile_number ===
        gameData?.data?.owner_details?.mobile_number,
    );
    setUserDetailsData(userDetails);
  };

  useEffect(() => {
    setLoading(false);
    if (studyDetails?.data) {
      updateUserData(studyDetails);

      const allPlayers = [
        {
          ...studyDetails?.data?.owner_details,
          pgn: studyDetails?.data?.pgn,
          isDeletable: false,
          role: 'owner',
        },
        ...studyDetails?.data?.participants?.map(i => ({
          ...i,
          isDeletable: true,
        })),
      ];

      setParticipantsList(allPlayers);
      if (studyDetails.data.starting_position === 'start') {
        studyDetails.data.starting_position = STARTING_FEN;
      }
      let allChapters = studyDetails?.data?.chapters?.map(chapter => ({
        ...chapter,
        fen: sanitizeFen(chapter?.fen),
      }));
      setChapterList(allChapters);
      const firstChapter = allChapters?.[0];
      const matchingChapter = selectedChapter
        ? allChapters?.find(chapter => chapter?.id === selectedChapter?.id)
        : null;
      if (firstChapter && (!selectedChapter || !matchingChapter)) {
        setSelectedChapter(firstChapter);
        setBoardOrientation(
          firstChapter?.orientation === 'W' ? 'white' : 'black',
        );
      } else if (matchingChapter) {
        // Refresh selectedChapter with the latest fetched fields (e.g.
        // chapter_mode, name, orientation) so settings saved via Chapter
        // Settings take effect immediately. Board orientation is left
        // untouched here so a manual board flip isn't reset by every
        // websocket-triggered refetch.
        setSelectedChapter(matchingChapter);
      }
      if (newChapterCreated) {
        const lastChapter =
          studyDetails?.data?.chapters?.[
            studyDetails?.data?.chapters?.length - 1
          ];
        if (lastChapter) {
          setSelectedChapter(lastChapter);
          setBoardOrientation(
            lastChapter.orientation === 'W' ? 'white' : 'black',
          );
        }
        setNewChapterCreated(false);
      }
      setSelectedUser(allPlayers?.[0]);
    } else {
      // navigation?.navigate(ScreenName?.Dashboard);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyDetails]);

  useEffect(() => {
    if (!selectedChapter?.id || !chapterMovesResponse?.data) {
      return;
    }
    const chapterData = chapterMovesResponse.data;
    if (chapterData.chapter_id !== selectedChapter.id) {
      return;
    }

    let parsedMovesForPgn: any = null;
    if (chapterData.moves?.length > 0) {
      parsedMovesForPgn = chapterData.moves.map((move, index) => ({
        ...move,
        move: move?.san,
        newFen: move?.fen,
        isMainBranch: true,
        lastFen:
          chapterData.moves?.[index - 1]?.fen || sanitizeFen(chapterData.fen),
      }));
    } else if (chapterData?.pgn) {
      const startignFenFromPgn = getStartingFenFromPgn(
        cleanPgn(chapterData?.pgn),
      );
      parsedMovesForPgn = generateMoveData(
        startignFenFromPgn || STARTING_FEN,
        cleanPgn(chapterData?.pgn),
      );
    } else {
      parsedMovesForPgn = [];
    }

    setMovesList(prev => ({...prev, [selectedChapter.id]: parsedMovesForPgn}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterMovesResponse, selectedChapter?.id]);

  // Shared by a real correct move in lesson mode and by "View the solution" -
  // jumps to the matched tree node, shows the feedback message, clears the
  // hint/solution affordances (fresh position, nothing to reveal yet), and
  // auto-plays the opponent's reply if it's now their turn.
  const advanceLessonAfterCorrectMove = useCallback(
    (matchedMove: any, message: string) => {
      const chapterId = selectedChapter?.id;
      setmoveSelected(matchedMove);
      setLessonFeedback({status: 'success', message});
      setLessonHintRevealed(false);
      setLessonHasWrongAttempt(false);

      const studentColor = selectedChapter?.orientation === 'B' ? 'b' : 'w';
      const currentTurn = matchedMove.fen?.split(' ')[1];
      if (currentTurn !== studentColor) {
        const nextMainMoves = movesList[chapterId] || [];
        const matchIdx = nextMainMoves.findIndex(
          (m: any) => m.id === matchedMove.id,
        );
        let opponentMove = null;
        if (matchIdx !== -1 && matchIdx + 1 < nextMainMoves.length) {
          opponentMove = nextMainMoves[matchIdx + 1];
        } else if (
          matchedMove.child_moves &&
          matchedMove.child_moves.length > 0
        ) {
          opponentMove = matchedMove.child_moves[0];
        }

        if (opponentMove) {
          const opponentReply = opponentMove;
          setTimeout(() => {
            setmoveSelected(opponentReply);
            // If the coach commented the reply, let that narration take
            // over the feedback card (its idle state reads the current
            // node's annotation) instead of a now-stale success message.
            if (opponentReply.annotation) {
              setLessonFeedback({status: 'idle', message: ''});
            }
          }, 800);
        }
      }
    },
    [selectedChapter?.id, selectedChapter?.orientation, movesList],
  );

  const handleRevealLessonHint = useCallback(() => {
    setLessonHintRevealed(true);
  }, []);

  const handleViewLessonSolution = useCallback(() => {
    const chapterId = selectedChapter?.id;
    if (!chapterId) {
      return;
    }
    const currentMoves = movesList[chapterId] || [];
    const {good} = getNextMoveOptions(currentMoves, moveSelected);
    if (good.length === 0) {
      return;
    }
    const solutionMove = good[0];
    const explanation = solutionMove.success_message || solutionMove.annotation;
    advanceLessonAfterCorrectMove(
      solutionMove,
      `Solution: ${solutionMove.san}.${explanation ? ` ${explanation}` : ''}`,
    );
  }, [
    selectedChapter?.id,
    movesList,
    moveSelected,
    advanceLessonAfterCorrectMove,
  ]);

  const handlePositionChange = async tempPosition => {
    const chapterId = selectedChapter?.id;
    if (!chapterId) {
      return;
    }

    const {newFen, lastFen, san} = tempPosition;
    const currentMoves = movesList[chapterId] || [];

    // Only the Interactive Lesson (gamebook) mode gates moves through
    // correctness validation. "Hide next moves" is a pure UI-concealment
    // setting on Lichess — any legal move is playable there, same as
    // Normal analysis; only the move *list* stays concealed (WorkbookMovesList's
    // hideMode/truncateMovesForReveal). Once the student has completed the
    // chapter (isChapterCompleted), the gamebook is done its job - drop into
    // free play for both colors, same as Normal analysis, instead of
    // continuing to validate against the authored line.
    const isLessonValidatedMode =
      selectedChapter?.chapter_mode === 'lesson' && !isChapterCompleted;
    if (isLessonValidatedMode && !effectiveIsOwner) {
      const {good, bad} = getNextMoveOptions(currentMoves, moveSelected);
      const findMatch = (list: any[]) =>
        list.find(
          m =>
            m.san?.toLowerCase() === san?.toLowerCase() ||
            isSamePosition(m.fen, newFen),
        );

      const goodMatch = findMatch(good);
      const badMatch = !goodMatch ? findMatch(bad) : null;

      if (goodMatch) {
        // Flag it as a variation the instant the student steps onto it, not
        // just once it dries up - so "this is an alternate line" is obvious
        // from the first move, before isAlternateBranchDeadEnd ever fires.
        const defaultMessage = isOnMainline(currentMoves, goodMatch)
          ? 'Correct move!'
          : "Good alternative! You're on a variation - let's play it out.";
        advanceLessonAfterCorrectMove(
          goodMatch,
          goodMatch.success_message || goodMatch.annotation || defaultMessage,
        );
        return;
      }

      // Wrong move: matches lila's gamebookPlayCtrl - actually show the
      // move played on the board (a transient node, not part of the
      // authored tree), display the feedback, then auto-rewind to the
      // pre-move position after 800ms. Capture the pre-move position now,
      // since moveSelected will change the instant we show the attempt.
      const positionBeforeAttempt = moveSelected;
      const attemptedMove = {
        id: `wrong-attempt_${uuid.v4()}`,
        san,
        move: san,
        fen: newFen,
        lastFen,
        isTransientWrongAttempt: true,
      };
      setmoveSelected(attemptedMove);
      setLessonHasWrongAttempt(true);

      if (badMatch) {
        // A pre-authored wrong alternative (Blunder/Mistake/Dubious move) —
        // show its own comment explaining why. This deviation text is shown
        // automatically (matches Lichess: a sub-variation's own comment is
        // always its wrong-move feedback), unlike the hint below, which is
        // only revealed on request.
        setLessonFeedback({
          status: 'error',
          message: badMatch.annotation || 'That move is a mistake. Try again!',
        });
      } else {
        // No authored variation matches this exact wrong move - fall back
        // to the position's deviation_message (lila's "Explain why all
        // other moves are wrong" field, authored once per position rather
        // than per wrong move) if the author wrote one, else a generic
        // message. Unlike Lichess's on-demand "Get a hint" button, this
        // used to auto-surface moveSelected.hint as the error message
        // itself - the hint now stays hidden until the student explicitly
        // asks for it (renderLessonFeedbackCard), matching
        // gamebookPlayCtrl's showHint toggle.
        setLessonFeedback({
          status: 'error',
          message: good[0]?.deviation_message || 'Incorrect move. Try again!',
        });
      }

      setTimeout(() => {
        setmoveSelected(current =>
          current?.id === attemptedMove.id ? positionBeforeAttempt : current,
        );
      }, 800);
      return;
    }

    // Practice with Computer never touches the authored tree: the attempt is
    // its own linear line, owned by usePracticeGame. That's what lets the
    // student play any legal move from the start position (the tree's
    // fixed-first-move rule used to reject both their move and the engine's),
    // and what makes Retry a genuinely fresh game.
    //
    // playStudentMove also refuses moves that aren't the student's to make -
    // the engine's pieces, or a second move squeezed in while the reply is
    // still being searched. Returning without applying anything leaves the
    // board briefly desynced, so force a re-render of the real position to
    // snap it back, exactly like the 'blocked' path below.
    if (isPracticeMode) {
      const accepted = practice.playStudentMove({san, newFen, lastFen});
      if (!accepted) {
        // Re-setting our own state is NOT enough to pull the board back: it
        // holds its own chess.js instance and only resyncs when the `position`
        // or `lastMoveFen` STRINGS change, which they don't here. Bump
        // `resetChessboard` as well - the escape hatch the board package
        // provides for exactly this (see ChessAnalysis, which uses the same
        // prop). The refusal should normally have been caught earlier by
        // handleBoardPieceDrop; this is the backstop.
        setmoveSelected(prev => (prev ? {...prev} : prev));
        setBoardResyncNonce(n => n + 1);
      }
      return;
    }

    // Register the move against the tree in one pure step — extend the
    // mainline, navigate to an existing node, insert a new variation, or
    // reject it (different first move from a fixed start position). All the
    // tree-shape rules live in utils/moveTreeOps.ts, which is unit-tested;
    // this handler only applies the result to state and kicks off the save.
    const result = addMoveToTree(currentMoves, {
      lastFen,
      newFen,
      san,
      tempId: `${newFen}_${uuid.v4()}`,
    });

    if (result.kind === 'blocked') {
      // Force a re-render of the authored position so the board snaps back
      // instead of staying desynced on the rejected move.
      showToast(
        'This chapter starts from a fixed position — its first move is set.',
        {
          type: 'Error',
        },
      );
      setmoveSelected(prev => (prev ? {...prev} : prev));
      return;
    }

    const node = result.node as any;
    const selection = {
      ...node,
      newFen: node.fen,
      lastFen: node.lastFen || lastFen,
      move: node.san,
    };

    if (result.kind === 'navigate') {
      // The move already exists in the tree — select it, so the move-list
      // highlight, comment box, annotations, and lesson gating all follow
      // the board. (Replaying an existing move used to leave the selection
      // stale on the previous move.)
      setmoveSelected(selection);
      return;
    }

    setMovesList(prev => ({...prev, [chapterId]: result.tree}));
    setmoveSelected(selection);

    if (effectiveIsOwner) {
      saveMove({
        tempId: node.id,
        chapter_id: chapterId,
        parent_id: node.parent ?? moveSelected?.id,
        san,
        fen: newFen,
      }).then(saved => {
        if (saved?.move_id) {
          // Swap the temp id for the persisted one everywhere — the node
          // itself, children that captured it as `parent`, and the current
          // selection.
          setMovesList(prev => ({
            ...prev,
            [chapterId]: replaceNodeId(
              prev[chapterId] || [],
              node.id,
              saved.move_id,
            ),
          }));
          setmoveSelected(prev =>
            prev?.id === node.id ? {...prev, id: saved.move_id} : prev,
          );
        }
      });
    }
  };

  /**
   * Pre-move gate for the board, wired to `onPieceDropExtension` — which the
   * board calls BEFORE advancing its internal chess.js. Returning truthy means
   * "stop, I'll drive the position from above".
   *
   * This is what actually stops a student dragging the computer's pieces.
   * Rejecting afterwards (in handlePositionChange) could not work: by then the
   * board had already applied the move to its own game, and nothing the screen
   * set would bring it back — so the illegal move stayed on screen and every
   * later move was reported against a position the attempt had never agreed
   * to. Same contract the puzzle boards use; see FunChallenges/docs/pdb_flow.md.
   */
  // Pulled out of `practice` so the memos below depend on the stable callbacks
  // themselves rather than on the hook's result object, which is a new
  // identity every render.
  const {canStudentMoveFrom: practiceCanMoveFrom, takeBack: practiceTakeBack} =
    practice;

  const handleBoardPieceDrop = useCallback(
    (from: string) => {
      if (!isPracticeMode) {
        return false;
      }
      return !practiceCanMoveFrom(from);
    },
    [isPracticeMode, practiceCanMoveFrom],
  );

  /* ─── Board navigation during a practice attempt ───────────────────────
   * The attempt owns its own linear line, whose nodes are not in the chapter
   * tree - so every tree-based nav handler silently no-ops on them (getPrevMove
   * looks the id up, misses, and returns null). The back arrow and the ← key
   * were therefore dead controls mid-game, while the panel's "Take back"
   * button did the same job right next to them; and on a COMPLETED chapter
   * (where isForwardNavBlocked lifts) Next/Last jumped the board onto the
   * authored tree while the attempt was still empty, only to be yanked back by
   * usePracticeGame's reset-to-start effect. Route them at the attempt. */
  const handleToolbarPrevMove = useCallback(() => {
    if (isPracticeMode) {
      // Goes through takeBack, so it is counted and bounded by the coach's
      // take-back policy exactly like the panel button - rather than being the
      // free, invisible rewind the back arrow used to be.
      practiceTakeBack();
      return;
    }
    handlePrevMove();
  }, [isPracticeMode, practiceTakeBack, handlePrevMove]);

  const isPracticeNavBlocked = isPracticeMode;
  const handleToolbarNextMove = useCallback(() => {
    if (isPracticeNavBlocked) {
      return false;
    }
    return handleNextMove();
  }, [isPracticeNavBlocked, handleNextMove]);

  const handleToolbarLastMove = useCallback(() => {
    if (isPracticeNavBlocked) {
      return;
    }
    handleLastMove();
  }, [isPracticeNavBlocked, handleLastMove]);

  const isForwardNavBlockedEffective =
    isForwardNavBlocked || isPracticeNavBlocked;

  // Practice attempt state (outcome, verdict, move count, the computer's
  // replies) all comes from usePracticeGame above. Kept as local aliases so
  // the render section below reads the same as it did before the extraction.
  const practiceOutcome = practice.outcome;
  const practiceVerdict = practice.verdict;
  const practiceMoveCount = practice.studentMoveCount;

  // Force-enable the engine on entering a practice-mode chapter, so the
  // student doesn't have to manually flip the Engine Evaluation switch.
  useEffect(() => {
    if (isPracticeMode) {
      setEnableStockfish(true);
    }
  }, [isPracticeMode, selectedChapter?.id, setEnableStockfish]);

  const renderScene = (rendererSceneProps: any) => {
    const {route: tempRoute} = rendererSceneProps;
    // let returnJSX = <></>;
    switch (tempRoute.key) {
      case 'PARTICIPANTS':
        return (
          // <ScrollView style={{flex: 1, paddingVertical: 10}}>
          <Participants
            participantsList={participantsList}
            userDetailsData={userDetailsData}
            gameId={gameId}
            setLoading={setLoading}
            refetchStudy={refetchStudy}
            isOwner={isOwner}
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            setOpenRemoveConfimation={setOpenRemoveConfimation}
          />
          // </ScrollView>
        );
      case 'CHAPTERS':
        return (
          // <ScrollView style={{flex: 1, paddingVertical: 10}}>
          <Chapters
            chaptersList={chapterList}
            gameId={gameId}
            setLoading={setLoading}
            refetchStudy={refetchStudy}
            isOwner={isOwner}
            selectedChapter={selectedChapter}
            setSelectedChapter={setSelectedChapter}
            setNewChapterCreated={setNewChapterCreated}
            setBoardOrientation={setBoardOrientation}
            setOpenRemoveConfimation={setOpenRemoveConfimation}
            // renderScene is shared between mobile's TabView and desktop's
            // chapter flyout - only desktop has the Workbook panel's
            // Settings flyout to open, so mobile leaves this undefined and
            // keeps its own local modal (see Chapters/index.tsx).
            onOpenChapterSettings={
              isMobileOrTab ? undefined : () => setWorkbookTab('settings')
            }
            boardFen={moveSelected?.fen || currentChapterFen}
            boardPositionLabel={
              !moveSelected?.id || moveSelected?.id === 'start'
                ? 'The chapter start.'
                : `After ${
                    moveSelected?.san || moveSelected?.move || 'this move'
                  }.`
            }
          />
          // </ScrollView>
        );

      default:
        return null;
    }
  };

  // Same scenes renderScene serves to mobile's TabView / desktop's flyouts,
  // wrapped in the {key, label, component} shape CCTabs expects.
  const playerOrChaptersTab = [
    {
      key: 0,
      label: 'Chapters',
      component: () => renderScene({route: {key: 'CHAPTERS'}}),
    },
    {
      key: 1,
      label: 'Participants',
      component: () => renderScene({route: {key: 'PARTICIPANTS'}}),
    },
  ];

  const getCorrectNextMoves = useCallback(() => {
    const chapterId = selectedChapter?.id;
    if (!chapterId) {
      return [];
    }
    const currentMoves = movesList[chapterId] || [];
    return getNextMoveOptions(currentMoves, moveSelected).good;
  }, [selectedChapter?.id, movesList, moveSelected]);

  // Resume the student's last position for this lesson chapter, once per
  // chapter visit, as soon as both the saved-progress lookup and the move
  // tree have loaded. Must run (and mark itself done) even when there's no
  // saved progress, so the save-effect below doesn't start persisting until
  // this had a chance to apply a resume first.
  useEffect(() => {
    if (!isLessonProgressMode || !selectedChapter?.id) {
      return;
    }
    if (resumedProgressChapterIdRef.current === selectedChapter.id) {
      return;
    }
    const moves = movesList[selectedChapter.id];
    if (!moves || chapterProgressResponse === undefined) {
      return;
    }
    resumedProgressChapterIdRef.current = selectedChapter.id;

    const savedMoveId = chapterProgressResponse?.data?.move_id;
    if (!savedMoveId) {
      return;
    }
    const findById = (nodes: any[]): any => {
      for (const node of nodes) {
        if (node.id === savedMoveId) {
          return node;
        }
        if (node.child_moves?.length) {
          const found = findById(node.child_moves);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };
    const resumedMove = findById(moves);
    if (resumedMove) {
      setmoveSelected(resumedMove);
    }
  }, [
    isLessonProgressMode,
    selectedChapter?.id,
    chapterProgressResponse,
    movesList,
  ]);

  // Persist progress on every position change in lesson mode, but only
  // after the resume effect above has run for this chapter (otherwise the
  // very first render's default "start" position would overwrite a real
  // saved position before it has a chance to load). Stops once the chapter
  // is already completed - post-completion moves are free exploration (see
  // isLessonValidatedMode above), not authored-tree progress, and their
  // move ids are local temp ids that don't exist as a StudyMove on the
  // backend, so posting them here would just 404.
  useEffect(() => {
    if (
      !isLessonProgressMode ||
      !selectedChapter?.id ||
      !moveSelected ||
      isChapterCompleted
    ) {
      return;
    }
    if (resumedProgressChapterIdRef.current !== selectedChapter.id) {
      return;
    }
    const isAtStart = moveSelected.id === 'start';
    const completed = !isAtStart && getCorrectNextMoves().length === 0;
    saveChapterProgress(
      {
        chapterId: selectedChapter.id,
        move_id: isAtStart ? null : moveSelected.id,
        completed,
      },
      // Refetch progress so isChapterCompleted/isChapterLocked flip and the
      // Analysis tab unlocks immediately - useGetChapterProgress's 30s
      // staleTime means it otherwise wouldn't pick this up until the next
      // window focus or chapter switch.
      completed ? {onSuccess: () => refetchChapterProgress()} : undefined,
    );
  }, [
    isLessonProgressMode,
    selectedChapter?.id,
    moveSelected,
    isChapterCompleted,
    saveChapterProgress,
    getCorrectNextMoves,
    refetchChapterProgress,
  ]);

  // Beyond Lichess parity, same as lesson progress above: record how the
  // student's most recent attempt went. EVERY terminal outcome is recorded -
  // StudyChapterProgress.result/move_count exist precisely for this, and only
  // persisting wins left them stuck at 'ongoing'/null after a loss or draw.
  // Only a win sets `completed`, so the unlock semantics (Analysis tab,
  // engine assist, forward nav) are exactly what they were.
  //
  // Keyed on the attempt's terminal status rather than the chapter alone, so
  // a second attempt in the same session is recorded too - the old ref was
  // per-chapter and silently swallowed every attempt after the first.
  const practiceRecordedRef = useRef<string | null>(null);
  useEffect(() => {
    // Sandbox games are never assessed and never recorded - not on a win, not
    // ever, and not even when armed from the chapter's own start position.
    // Grading happens only through the chapter's Exercise. One rule, no edge
    // cases about which ad-hoc game "counts".
    if (isSandboxActive) {
      return;
    }
    if (!isExerciseChapter || !selectedChapter?.id) {
      return;
    }
    if (practiceOutcome.status === 'ongoing') {
      return;
    }
    // attemptNonce makes two otherwise-identical attempts distinct - without
    // it, losing twice in the same number of moves recorded only the first,
    // undercounting the `attempts` a coach now reads.
    const attemptKey = `${selectedChapter.id}:${practice.attemptNonce}:${practiceOutcome.status}:${practiceMoveCount}`;
    if (practiceRecordedRef.current === attemptKey) {
      return;
    }
    practiceRecordedRef.current = attemptKey;
    const won = practiceOutcome.status === 'won';
    if (won) {
      playPracticeSuccessSound();
    }
    saveChapterProgress(
      {
        chapterId: selectedChapter.id,
        move_id: null,
        completed: won,
        move_count: practiceMoveCount,
        result: practiceOutcome.status,
        // A win with eleven take backs isn't the same as a clean one, so the
        // count travels with the result. `attempts` is deliberately NOT sent:
        // the server increments it off the terminal result, so a refresh
        // mid-attempt can't inflate it.
        takebacks_used: practice.takebackCount,
      },
      won ? {onSuccess: () => refetchChapterProgress()} : undefined,
    );
  }, [
    isSandboxActive,
    isExerciseChapter,
    selectedChapter?.id,
    practiceOutcome.status,
    saveChapterProgress,
    practiceMoveCount,
    practice.takebackCount,
    practice.attemptNonce,
    playPracticeSuccessSound,
    refetchChapterProgress,
  ]);

  useEffect(() => {
    practiceRecordedRef.current = null;
    setDismissedPracticeEndCard(false);
  }, [selectedChapter?.id]);

  /* ─── Lesson authoring panel (owner, lesson mode) ─────────────────────
   * The bottom half of the split workbook Moves tab: selecting any move or
   * branch shows, for that node, a role selector (Perfect move / Alternate
   * move / Mistake–Blunder) plus the comment fields that role needs —
   * mirroring lila's gamebookEdit, which likewise swaps its helper fields
   * based on what the selected node is (player move / opponent move /
   * start). Roles map onto the existing tree semantics rather than a new
   * flag: mainline = the perfect move, a variation = an acceptable
   * alternate, a variation carrying a Blunder/Mistake/Dubious glyph = a
   * wrong answer (see lessonMoves.ts's isBadMove). */

  const lessonFlatById = useMemo(
    () => flattenMoveTree(movesList?.[selectedChapter?.id as any]),
    [movesList, selectedChapter?.id],
  );

  const isMainlineSelected = useMemo(() => {
    const currentMoves = movesList?.[selectedChapter?.id as any] || [];
    return currentMoves.some((m: any) => m.id === moveSelected?.id);
  }, [movesList, selectedChapter?.id, moveSelected?.id]);

  const selectedLessonRole: LessonMoveRole = isMainlineSelected
    ? 'perfect'
    : isBadMove(moveSelected)
    ? 'bad'
    : 'alternate';

  const handleSetLessonRole = (newRole: LessonMoveRole) => {
    if (!moveSelected?.id || newRole === selectedLessonRole) {
      return;
    }
    // A mainline move can't be demoted in place — some other continuation
    // has to take its spot first (same as Lichess, where you'd promote the
    // better line). The buttons are disabled in that state; this is just a
    // guard.
    if (isMainlineSelected) {
      return;
    }
    if (newRole === 'perfect') {
      if (isBadMove(moveSelected)) {
        handleSaveAnnotationIcon('');
      }
      moveUndo.promoteMove(
        moveSelected.id,
        lessonFlatById,
        `${moveSelected.san || 'Move'} promoted to mainline`,
      );
    } else if (newRole === 'alternate') {
      handleSaveAnnotationIcon('');
    } else if (newRole === 'bad') {
      handleSaveAnnotationIcon('Mistake');
    }
  };

  // Split out of the toolbar (below) so the desktop "Stage + Workspace"
  // layout can place nav arrows under the board and the tool-selector row
  // inside the Edit tools slide-over separately, while mobile still gets
  // the exact same combined toolbar via renderBoardNavigationToolbar.
  // Board toolbar (below the board, alongside the nav arrows): flip is a
  // plain unconditional action; annotations/lesson/sync/preview keep their
  // existing owner-only gating. Help lives in the header now (see the
  // pencil icon's old spot) since Annotations/Lesson each slide up from
  // their own icon instead of sharing the pencil's flyout. Order given
  // right-to-left, so this is rendered as `row-reverse` with the buttons
  // listed in that literal order.
  const isLessonMode = selectedChapter?.chapter_mode === 'lesson';
  // Deliberately Exercise-only, NOT `isPracticeMode`: an Exercise takes over
  // the Moves panel because the chapter *is* the game, whereas a Sandbox is a
  // tool the viewer reached for and lives in the Tools tab, leaving the
  // authored move list where it was.
  const isPracticeModeView =
    selectedChapter?.chapter_mode === 'practice' && !effectiveIsOwner;

  // Mirrors renderLessonFeedbackCard's own isSolved check - lifted to
  // component scope (rather than only computed inside the render function)
  // so the space-bar-advance effect below can react to it too, matching
  // lila's gamebookPlayCtrl: space bar in the 'end' state goes to the next
  // chapter.
  //
  // A variation is an acceptable detour, not a second correct ending - so
  // running out of authored moves only counts as "solved" while on the
  // actual mainline. Running dry inside a variation is handled separately
  // below (isAlternateBranchDeadEnd), which rewinds to the branch point
  // instead of ending the lesson.
  const lessonIsSolved = Boolean(
    isLessonMode &&
      !effectiveIsOwner &&
      lessonFeedback.status !== 'error' &&
      moveSelected &&
      moveSelected.id !== 'start' &&
      !moveSelected.isTransientWrongAttempt &&
      isOnMainline(movesList[selectedChapter?.id] || [], moveSelected) &&
      getCorrectNextMoves().length === 0,
  );

  const isAlternateBranchDeadEnd = Boolean(
    isLessonMode &&
      !effectiveIsOwner &&
      lessonFeedback.status !== 'error' &&
      moveSelected &&
      moveSelected.id !== 'start' &&
      !moveSelected.isTransientWrongAttempt &&
      !isOnMainline(movesList[selectedChapter?.id] || [], moveSelected) &&
      getCorrectNextMoves().length === 0,
  );

  // A "good" variation the student played through has run out of authored
  // moves - rather than declaring the lesson solved (that's reserved for the
  // mainline), tell the student to go find the actual mainline move and
  // rewind the board back to where they left it, so the normal
  // handlePositionChange gating picks the mainline continuation back up.
  useEffect(() => {
    if (!isAlternateBranchDeadEnd) {
      return undefined;
    }
    setLessonFeedback({
      status: 'branch-end',
      message: 'End of this alternate branch. Now find the main line move.',
    });
    const currentMoves = movesList[selectedChapter?.id] || [];
    const branchPoint = getMainlineBranchPoint(currentMoves, moveSelected);
    const timer = setTimeout(() => {
      setmoveSelected(
        !branchPoint || branchPoint.rewindTo === 'start'
          ? ({
              move: '',
              fen: selectedChapter?.fen || STARTING_FEN,
              lastFen: selectedChapter?.fen || STARTING_FEN,
              id: 'start',
            } as any)
          : branchPoint.rewindTo,
      );
      setLessonFeedback({status: 'idle', message: ''});
    }, 1200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAlternateBranchDeadEnd]);

  useEffect(() => {
    // Also fires on a practice-mode win, matching the same "space bar
    // advances at the end state" pattern lila uses for gamebook.
    if (!lessonIsSolved && practiceOutcome.status !== 'won') {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) {
        return;
      }
      if (event.key !== ' ' && event.key !== 'Spacebar') {
        return;
      }
      event.preventDefault();
      if (hasNextChapter) {
        handleGoToNextChapter();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    lessonIsSolved,
    practiceOutcome.status,
    hasNextChapter,
    handleGoToNextChapter,
  ]);

  // Space toggles autoplay, like lichess's replay. Deliberately a separate
  // listener from the end-of-lesson space handler above: when that one is
  // active (solved lesson / won practice) space means "next chapter", so
  // this one stands down. Forward-blocked modes can't autoplay at all.
  useEffect(() => {
    if (
      isForwardNavBlocked ||
      lessonIsSolved ||
      practiceOutcome.status === 'won'
    ) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
          (target as any).isContentEditable)
      ) {
        return;
      }
      if (event.key !== ' ' && event.key !== 'Spacebar') {
        return;
      }
      event.preventDefault();
      setIsAutoplayOn(prev => !prev);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    isForwardNavBlocked,
    lessonIsSolved,
    practiceOutcome.status,
    setIsAutoplayOn,
  ]);

  const tabDetails = [
    {
      key: 0,
      label: 'Details',
      component: () => leftComponent,
    },
    {
      key: 1,
      label:
        isLessonMode && !effectiveIsOwner
          ? 'Lesson'
          : isPracticeModeView
          ? 'Practice'
          : 'Moves',
      component: () =>
        isLessonMode && !effectiveIsOwner ? (
          <Box style={{padding: 16}}>
            <LessonFeedbackCard
              positionComment={
                (!moveSelected?.isTransientWrongAttempt &&
                  moveSelected?.annotation) ||
                null
              }
              isSolved={lessonIsSolved}
              lessonFeedback={lessonFeedback}
              currentHint={getCorrectNextMoves()[0]?.hint}
              hintRevealed={lessonHintRevealed}
              hasWrongAttempt={lessonHasWrongAttempt}
              hasPendingMoves={getCorrectNextMoves().length > 0}
              onRevealHint={handleRevealLessonHint}
              onViewSolution={handleViewLessonSolution}
              onRetry={handleFirstMove}
              hasNextChapter={hasNextChapter}
              onNextChapter={handleGoToNextChapter}
            />
            <Spacer spacing={20} />
            <TouchableOpacity
              style={{
                backgroundColor: CCColors.Primary.Brown,
                padding: 12,
                borderRadius: 8,
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onPress={handleFirstMove}>
              <CCText color={CCColors.White} fontWeight="bold">
                Reset Lesson
              </CCText>
            </TouchableOpacity>
          </Box>
        ) : isPracticeModeView ? (
          renderPracticePanel(false)
        ) : (
          <>
            <Spacer spacing={10} />
            <WorkbookMovesList
              gameMoves={movesList?.[selectedChapter?.id as any]}
              activeMove={moveSelected}
              setmoveSelected={selectMoveFromList}
              setLoading={tempLoading => setLoading(tempLoading)}
              isOwner={isOwner}
              highlightedMoveIds={highlightedMoveIds}
              hideMode={isConcealMovesOn && !effectiveIsOwner}
              moveUndo={moveUndo}
              lessonMode={selectedChapter?.chapter_mode === 'lesson'}
              onNavigateStart={handleFirstMove}
              onSetMoveRole={(move, role) =>
                handleSaveAnnotationIcon(
                  role === 'bad' ? 'Mistake' : '',
                  move.id,
                )
              }
            />
          </>
        ),
    },
  ];

  const leftComponent = (
    <>
      <Box
        type="row"
        style={{
          alignItems: 'center',
          paddingVertical: 12,
          paddingHorizontal: 16,
          gap: 12,
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(38,35,34,0.08)',
        }}>
        <TouchableOpacity
          onPress={() => {
            if (navigation.canGoBack()) {
              navigation.goBack();
            } else {
              navigation.navigate(ScreenName.StudyList as any);
            }
          }}
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            backgroundColor: 'rgba(107, 88, 77, 0.05)',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Ionicons name="arrow-back" size={18} color="#4D3F37" />
        </TouchableOpacity>
        <CCText
          fontSize={FontSizes[15]}
          fontWeight="bold"
          numberOfLines={1}
          style={{flex: 1, color: '#262322'}}>
          {studyDetails?.data?.name}
        </CCText>
      </Box>
      <Box style={{flex: 1}}>
        <ConditionalRender
          condition={isMobileOrTab}
          childrenA={
            <TabView
              lazy
              //   style={styles.contentContainerStyle}
              // @ts-ignore
              navigationState={{index: index, routes}}
              renderScene={renderScene}
              renderTabBar={renderTabBarPill}
              onIndexChange={setIndex}
              initialLayout={{width: WINDOW_WIDTH / 2}}
              // style={{height: '75vh'}}
            />
          }
          childrenB={
            <CCTabs
              tabIndex={playerOrChaptersSelect}
              tabItems={playerOrChaptersTab}
              activeColor="#4D3F37"
              inactiveColor="#8a8988"
              indicatorStyle={{backgroundColor: '#4D3F37', height: 2}}
              handleTabChange={({route}) => {
                setPlayerOrChaptersSelect(parseInt(route.key, 10));
              }}
            />
          }
        />
      </Box>
      {isOwner && (
        <View style={styles.stickyButtonWrapper}>
          <View style={styles.syncRow}>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
              <MaterialCommunityIcons
                name={previewAsParticipant ? 'eye' : 'eye-outline'}
                size={16}
                color={
                  previewAsParticipant
                    ? CCColors.Workbook.GoldText
                    : CCColors.Workbook.Grey
                }
              />
              <CCText
                fontSize={12}
                fontWeight="semibold"
                color={
                  previewAsParticipant
                    ? CCColors.Workbook.GoldText
                    : CCColors.Workbook.Grey
                }>
                Preview as Participant
              </CCText>
            </View>
            <Switch
              trackColor={{
                false: updateColorWithOpacity(CCColors.Workbook.Brown, 0.16),
                true: CCColors.Green,
              }}
              thumbColor={CCColors.White}
              onValueChange={() =>
                setPreviewAsParticipant(!previewAsParticipant)
              }
              value={previewAsParticipant}
            />
          </View>
        </View>
      )}
    </>
  );
  // One practice surface, rendered by both layouts (`heading` is the only
  // thing that differed between them). Everything it needs comes from
  // usePracticeGame, so neither layout reads practice state directly.
  const renderPracticePanel = (
    heading: boolean,
    exit?: {onExit: () => void; exitLabel: string},
  ) => (
    <PracticePanel
      heading={heading}
      practiceOutcome={practiceOutcome}
      practiceVerdict={practiceVerdict}
      practiceGoal={practiceGoal}
      practiceMoveCount={practiceMoveCount}
      takebackCount={practice.takebackCount}
      isEngineThinking={practice.isEngineThinking}
      reachedEndOfLine={practice.reachedEndOfAuthoredLine}
      dismissedEndCard={dismissedPracticeEndCard}
      difficulty={practiceDifficulty}
      onChangeDifficulty={key => {
        // A Sandbox owns its own difficulty (re-arming from the same position
        // is the cleanest way to change it); an Exercise keeps the viewer's
        // override separate from the chapter's authored default.
        if (isSandboxActive) {
          setSandboxSession(prev =>
            prev ? {...prev, difficultyKey: key, nonce: prev.nonce + 1} : prev,
          );
          return;
        }
        setStudentDifficultyKey(key);
        practice.reset();
      }}
      canTakeBack={practice.canTakeBack}
      onTakeBack={practice.takeBack}
      onReset={practice.reset}
      onAnalyseLine={() => setDismissedPracticeEndCard(true)}
      // "Next chapter" belongs to an assessed Exercise, not to a game someone
      // started out of curiosity from a random position.
      hasNextChapter={!isSandboxActive && hasNextChapter}
      onNextChapter={handleGoToNextChapter}
      attemptMoves={practice.attemptMoves}
      activeMove={moveSelected}
      onSelectMove={practice.selectAttemptMove}
      setLoading={setLoading}
      onExit={exit?.onExit}
      exitLabel={exit?.exitLabel}
      // Coach policies bind the assessed Exercise only. A Sandbox is the
      // viewer's own game from a position they chose, so it stays free and
      // unlimited however the chapter happens to be configured - and the
      // owner is never assessed, so they aren't bound either.
      strengthPolicy={
        isSandboxActive || !isExerciseChapter
          ? null
          : selectedChapter?.practice_strength_policy ?? null
      }
      strengthFloorElo={
        isSandboxActive || !isExerciseChapter
          ? null
          : selectedChapter?.practice_difficulty ?? null
      }
      takebacksAllowed={
        isSandboxActive || !isExerciseChapter
          ? null
          : selectedChapter?.practice_takebacks_allowed ?? null
      }
      assistEnabled={practiceAssistEnabled}
      // Only offer the toggle where the engine UI is actually available to
      // this viewer. A student on an unsolved Exercise has no engine panel,
      // eval bar or arrows to begin with (showStockfishOption is false), so a
      // switch promising to reveal them would be a lie.
      onChangeAssistEnabled={
        showStockfishOption ? setPracticeAssistEnabled : undefined
      }
    />
  );

  // What the Sandbox card offers to play from, and who moves first there.
  const sandboxPositionFen =
    moveSelected?.fen || selectedChapter?.fen || currentChapterFen;
  const sandboxSideToMove: 'w' | 'b' =
    sanitizeFen(sandboxPositionFen).split(' ')[1] === 'b' ? 'b' : 'w';
  const sandboxPositionLabel =
    !moveSelected?.id || moveSelected?.id === 'start'
      ? 'the starting position'
      : `the position after ${
          moveSelected?.san || moveSelected?.move || 'this move'
        }`;
  // Rule: an assessed Exercise still in progress blocks the Sandbox, otherwise
  // it's a free hint machine - work the answer out in a sandbox game, then go
  // back and "solve" it. Owners are never assessed, so they always get it.
  // The copy used to say "finish or reset your practice attempt", which was
  // simply untrue: the gate is completion, and resetting the attempt does
  // nothing to lift it. Say what actually unlocks it.
  const sandboxBlockedReason =
    isExerciseChapter && !isChapterCompleted
      ? 'Complete this exercise first, then you can play free games from any position.'
      : null;

  // The coach's read side of an Exercise. Gated on effectiveIsOwner rather
  // than isOwner so preview-as-participant shows a faithful participant view -
  // the endpoint is owner-only regardless, so this is presentation, not the
  // access control.
  const showExerciseResults =
    effectiveIsOwner && selectedChapter?.chapter_mode === 'practice';
  const {
    data: progressRoster,
    isLoading: isRosterLoading,
    isError: isRosterError,
    refetch: refetchRoster,
  } = useGetChapterProgressRoster(selectedChapter?.id, showExerciseResults);

  // Off by default (and hidden) in lesson/practice chapters the student
  // hasn't completed yet - see isChapterLocked. Once completed (or for the
  // owner previewing), the engine assist becomes available like a normal
  // Analysis chapter.
  const showStockfishOption = effectiveIsOwner || !isChapterLocked;
  // The eval bar next to the board is driven by enableStockfish alone
  // (which practice mode force-enables internally so the computer opponent
  // can move), so gate its visibility separately on showStockfishOption -
  // otherwise a locked practice chapter would still flash the eval to the
  // student even though the CevalPanel/toggle stay hidden.
  // ...and hidden by default while a practice game is in progress. The eval
  // bar is the same class of hint as the best-move arrow (see engineAutoShapes
  // in useEngineAnalysis): a live "you're winning / you're losing" meter beside
  // a position you're being asked to play out. showStockfishOption alone didn't
  // cover it for the same three cases the arrows leaked through - a Sandbox on
  // an ordinary analysis chapter, the owner testing their own exercise, and a
  // completed chapter replayed with "Play again".
  //
  // The engine itself keeps running headlessly, so the opponent still moves
  // and eval-based goals (equalize / get winning) are still judged normally -
  // this only hides the readout, and only until the viewer opts back in.
  const showEngineUi =
    enableStockfish && showStockfishOption && !isAssistSuppressed;

  // The mobile and desktop layouts below render the same game-viewer tab
  // strip, eval bar, and board in two different tree shapes (mobile stacks
  // the tab strip above the eval-bar+board row; desktop nests it only above
  // the board, beside a separate eval-bar column) - so they can't share one
  // JSX subtree without changing either layout. What they *can* share is the
  // prop-derivation logic itself, which is identical in both places and was
  // previously duplicated by hand (a real risk: it's exactly the kind of
  // thing that silently drifts out of sync between the two branches).
  const renderGameViewerTabs = () => (
    <GameViewerTabs
      tabs={gameTabs}
      activeTabId={activeGameTabId}
      onSelectTab={setActiveGameTabId}
      onCloseTab={handleCloseGameTab}
      onCloseAll={handleCloseAllGameTabs}
      currentPlyIndex={activeGameTab?.plyIndex ?? -1}
      totalPlies={activeGameTotalPlies}
      onPrev={() => stepActiveGame(-1)}
      onNext={() => stepActiveGame(1)}
      isLoading={!!activeGameTab && activeGamePgnQuery.isLoading}
      isError={!!activeGameTab && activeGamePgnQuery.isError}
    />
  );

  const renderEvalBar = () => (
    <EvalBar
      visible={Boolean(enableStockfish && chessboardHeight && boardWidth)}
      boardWidth={boardWidth}
      evalResult={evalResult}
      flipped={boardOrientation === 'black'}
    />
  );

  // `includeAnnotation` preserves an existing difference between the two
  // layouts (desktop passes moveSelected's annotation icon to the board,
  // mobile doesn't) rather than silently unifying it away.
  const renderChessboard = (includeAnnotation: boolean) => (
    <ChessboardWithArrow
      position={
        activeGameTab
          ? activeGameFen || moveSelected?.fen || currentChapterFen
          : moveSelected?.fen || currentChapterFen
      }
      boardOrientation={boardOrientation}
      lastMoveFen={
        activeGameTab ? activeGameLastMoveFen : moveSelected?.lastFen
      }
      handleNewFen={activeGameTab ? undefined : handlePositionChange}
      onPieceDropExtension={handleBoardPieceDrop}
      resetChessboard={boardResyncNonce}
      disabled={!!activeGameTab || isSavingMoves || !selectedChapter}
      colorTheme={chessboardColorTheme}
      {...(includeAnnotation
        ? {annotation: moveSelected?.annotation_icon}
        : {})}
      shapes={
        activeGameTab || isChapterLocked ? [] : moveSelected?.shapes || []
      }
      autoShapes={engineAutoShapes}
      moveId={moveSelected?.id}
      onShapesChange={handleShapesChange}
      onBoardWidthChange={setBoardWidth}
    />
  );

  // ChessConfirm + HelpModal are byte-identical between the two layouts.
  // CCLoader is intentionally NOT included here - mobile's loading condition
  // also checks isSavingAnnotation and desktop's doesn't, a pre-existing
  // difference this refactor preserves rather than silently unifying.
  const renderConfirmAndHelpModal = () => (
    <>
      <ChessConfirm
        open={openRemoveConfimation?.flag}
        header={openRemoveConfimation?.title || 'Remove from the board?'}
        bodyTxt={openRemoveConfimation?.bodyTxt}
        piece={openRemoveConfimation?.piece || 'chess-pawn'}
        pieceRotation={openRemoveConfimation?.pieceRotation}
        accent="red"
        bullets={openRemoveConfimation?.bullets}
        confirmLabel={openRemoveConfimation?.confirmLabel || 'Yes, Remove'}
        confirmIcon="flag-variant"
        handleClose={() => setOpenRemoveConfimation({flag: false, bodyTxt: ''})}
        handleConfirmation={openRemoveConfimation?.callbackFunc}
      />
      <HelpModal
        visible={showShortcutsHelp}
        onClose={() => setShowShortcutsHelp(false)}
        activeTab={helpModalTab}
        onTabChange={setHelpModalTab}
      />
    </>
  );

  if (isMobileOrTab) {
    return (
      <Box style={styles.mobileContainer}>
        {cevalEngineElement}
        {threatEngineElement}
        {opponentEngineElement}
        <Box style={{padding: 10, flex: 1, justifyContent: 'space-between'}}>
          <Box style={{flex: 1, justifyContent: 'center'}}>
            <Box
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                height: boardWidth || undefined,
              }}
              onLayout={onChessboardLayout}>
              {showEngineUi && (
                <Box
                  style={{
                    width: 28,
                    height: boardWidth || undefined,
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginRight: 4,
                  }}>
                  {renderEvalBar()}
                </Box>
              )}
              <Box style={{flex: 1, height: boardWidth || undefined}}>
                {renderChessboard(false)}
              </Box>
            </Box>
            <CCText
              fontSize={10}
              color={CCColors.Workbook.GreyDisabled}
              textAlign="center">
              Right-click + drag to draw an arrow · Right-click a square to
              circle it
            </CCText>
          </Box>
          <Card style={{marginTop: 8}} padding={activeGameTab ? 8 : 0}>
            {activeGameTab ? (
              <Box
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  width: '100%',
                }}>
                {renderGameViewerTabs()}
              </Box>
            ) : (
              // The *Effective/toolbar* handlers (from usePracticeGame's
              // wrappers above) fall through to these exact plain ones - see
              // isForwardNavBlockedEffective and handleToolbarPrevMove/
              // NextMove/LastMove - whenever practice mode is off, so wiring
              // them here unconditionally changes nothing outside practice
              // and routes takebacks/nav through the attempt while it is on.
              <BoardNavigationToolbar
                navArrows={{
                  forwardBlocked: isForwardNavBlockedEffective,
                  onFirstMove: handleFirstMove,
                  onPrevMove: handleToolbarPrevMove,
                  onNextMove: handleToolbarNextMove,
                  onLastMove: handleToolbarLastMove,
                  isAutoplayOn,
                  onToggleAutoplay: () => setIsAutoplayOn(prev => !prev),
                  autoplaySpeed,
                  onSetAutoplaySpeed: ms => {
                    setAutoplaySpeed(ms);
                    setShowAutoplayMenu(false);
                  },
                  showSpeedMenu: showAutoplayMenu,
                  onToggleSpeedMenu: () => setShowAutoplayMenu(prev => !prev),
                }}
                openingName={openingName}
                toolIcons={{
                  annotationsActive: activeToolbarTool === 'annotations',
                  onSelectAnnotations: () =>
                    setActiveToolbarTool('annotations'),
                  onFlipBoard: handleFlipBoard,
                  onShowHelp: () => setShowShortcutsHelp(true),
                }}
              />
            )}
          </Card>
        </Box>
        {chessboardHeight && (
          <BottomSheet
            minHeight={WINDOW_HEIGHT - chessboardHeight - MOBILE_HEADER_HEIGHT}>
            <CCTabs
              tabIndex={tabSelected}
              tabItems={tabDetails}
              activeColor="#4D3F37"
              inactiveColor="#8a8988"
              indicatorStyle={{backgroundColor: '#4D3F37', height: 2}}
              handleTabChange={({route}) => {
                setTabSelected(parseInt(route.key, 10));
              }}
            />
          </BottomSheet>
        )}
        {/* Mobile previously never rendered the remove confirmation the
            Chapters/Participants tabs trigger - same ChessConfirm as the
            desktop return below. */}
        {renderConfirmAndHelpModal()}
        <CCLoader
          loading={
            loading ||
            isLoading ||
            isSavingAnnotation ||
            (!!selectedChapter && isChapterMovesLoading)
          }
        />
      </Box>
    );
  }

  const hasFeedbackCard =
    (selectedChapter?.chapter_mode === 'lesson' && !effectiveIsOwner) ||
    isPracticeModeView;
  // Trimmed from 110 - the board sits in a ScrollView, so this is a soft
  // "try to avoid scrolling to see the toolbar" preference, not a hard
  // limit. It's usually the height term, not the width one, that caps the
  // board's size on a typical wide desktop viewport, so shaving a bit more
  // off here (down from 90) - and dropping the right-click caption that
  // used to sit under the board - both free up room for it to grow into.
  // GameViewerTabs renders *inside* the existing command-bar row (see
  // renderBoardCommandBar) instead of a bar of its own above the board, so
  // opening/closing game tabs never changes this offset or the board's size.
  const verticalOffset = 82 + (hasFeedbackCard ? 60 : 0) + 10;

  const boardMargin = showEngineUi ? 40 : 0;
  // Kept in sync with boardStudySurface's own padding (now 0 - see
  // styles.ts) so the width formula below doesn't reserve space nobody's
  // actually spending.
  const boardSurfacePadding = 0;
  const availableWidth = parentWidth > 0 ? parentWidth : WINDOW_WIDTH;
  const availableHeight = parentHeight > 0 ? parentHeight : WINDOW_HEIGHT;
  // Workspace is the only fixed-width column now - chapters live behind the
  // header's breadcrumb flyout instead of a permanent 220px sidebar, so the
  // board gets that width back. Kept in sync with styles.workspace's width
  // (one Workbook panel - Moves/Analysis tabs - given the roomier 560px a
  // side-by-side pair of columns used to split between them).
  const WORKSPACE_WIDTH = 560;
  // A little breathing room on each side of the board column (matches the
  // stage's paddingLeft/paddingRight below) instead of the board sitting
  // flush against the back button / workspace divider.
  const BOARD_SIDE_PADDING = 32;
  const maxBoardWidth = Math.max(
    240,
    Math.min(
      availableWidth - WORKSPACE_WIDTH - BOARD_SIDE_PADDING - boardMargin,
      availableHeight - verticalOffset,
    ),
  );
  const maxBoardContainerWidth = maxBoardWidth + boardMargin;
  const maxBoardSurfaceWidth = maxBoardContainerWidth + boardSurfacePadding;
  const boardShellWidth = maxBoardSurfaceWidth + 4;

  return (
    <Box style={{flex: 1}}>
      {cevalEngineElement}
      {threatEngineElement}
      {opponentEngineElement}
      <Box style={styles.pageColumn}>
        <Box style={styles.header}>
          <Box style={styles.headerLeftCluster}>
            <TouchableOpacity
              onPress={() => {
                if (navigation.canGoBack()) {
                  navigation.goBack();
                } else {
                  navigation.navigate(ScreenName.StudyList as any);
                }
              }}
              style={styles.headerBackBtn}>
              <Ionicons
                name="arrow-back"
                size={18}
                color={CCColors.TextColor.Primary}
              />
            </TouchableOpacity>

            <Box style={{position: 'relative', zIndex: 1000}}>
              <TouchableOpacity
                onHoverIn={() => setIsChapterPillHovered(true)}
                onHoverOut={() => setIsChapterPillHovered(false)}
                onPress={() => {
                  setShowParticipantsFlyout(false);
                  setShowChapterFlyout(prev => !prev);
                }}
                style={[
                  styles.chapterBreadcrumb,
                  (isChapterPillHovered || showChapterFlyout) &&
                    styles.chapterBreadcrumbActive,
                ]}>
                <MaterialCommunityIcons
                  name="book-open-page-variant-outline"
                  size={13}
                  color={
                    isChapterPillHovered || showChapterFlyout
                      ? CCColors.Workbook.GoldText
                      : CCColors.Workbook.CreamGold
                  }
                />
                <CCText numberOfLines={1} style={styles.breadcrumbStudyName}>
                  {studyDetails?.data?.name}
                </CCText>
                <CCText style={styles.breadcrumbSep}>›</CCText>
                <CCText numberOfLines={1} style={styles.breadcrumbChapterName}>
                  {selectedChapter?.name || 'Select a chapter'}
                </CCText>
                <Ionicons
                  name={showChapterFlyout ? 'chevron-up' : 'chevron-down'}
                  size={12}
                  color={
                    isChapterPillHovered || showChapterFlyout
                      ? CCColors.Workbook.GoldText
                      : CCColors.Workbook.CreamGold
                  }
                />
              </TouchableOpacity>

              {showChapterFlyout && (
                <Box
                  style={[
                    styles.chapterFlyout,
                    {
                      opacity: chapterFlyoutEntered ? 1 : 0,
                      transform: [{translateY: chapterFlyoutEntered ? 0 : -6}],
                    },
                  ]}>
                  <Box style={styles.dropdownHeader}>
                    <Box style={styles.dropdownHeaderTitleGroup}>
                      <MaterialCommunityIcons
                        name="book-open-page-variant-outline"
                        size={15}
                        color={CCColors.Workbook.GoldText}
                      />
                      <CCText style={styles.dropdownHeaderTitle}>
                        Chapters
                      </CCText>
                    </Box>
                    <Box style={styles.dropdownCountBadge}>
                      <CCText style={styles.dropdownCountText}>
                        {chapterList?.length || 0}
                      </CCText>
                    </Box>
                  </Box>
                  <CheckeredStrip height={5} />
                  <Box style={styles.chapterFlyoutBody}>
                    {renderScene({route: {key: 'CHAPTERS'}})}
                  </Box>
                </Box>
              )}
            </Box>

            <Box style={{flex: 1}} />

            <Box style={{position: 'relative', zIndex: 1000}}>
              <TouchableOpacity
                onHoverIn={() => setIsParticipantPillHovered(true)}
                onHoverOut={() => setIsParticipantPillHovered(false)}
                onPress={() => {
                  setShowChapterFlyout(false);
                  setShowParticipantsFlyout(prev => !prev);
                }}
                style={[
                  styles.participantPill,
                  (isParticipantPillHovered || showParticipantsFlyout) &&
                    styles.participantPillActive,
                ]}>
                <Ionicons
                  name="people-outline"
                  size={14}
                  color={
                    isParticipantPillHovered || showParticipantsFlyout
                      ? CCColors.Workbook.GoldText
                      : CCColors.Workbook.CreamGold
                  }
                />
                <CCText
                  fontSize={11}
                  color={
                    isParticipantPillHovered || showParticipantsFlyout
                      ? CCColors.Workbook.GoldText
                      : CCColors.Workbook.CreamGold
                  }
                  fontWeight="semibold">
                  {participantsList?.length || 0}
                </CCText>
                <Ionicons
                  name={showParticipantsFlyout ? 'chevron-up' : 'chevron-down'}
                  size={12}
                  color={
                    isParticipantPillHovered || showParticipantsFlyout
                      ? CCColors.Workbook.GoldText
                      : CCColors.Workbook.CreamGold
                  }
                />
              </TouchableOpacity>

              {showParticipantsFlyout && (
                <Box
                  style={[
                    styles.participantsFlyout,
                    {
                      opacity: participantsFlyoutEntered ? 1 : 0,
                      transform: [
                        {translateY: participantsFlyoutEntered ? 0 : -6},
                      ],
                    },
                  ]}>
                  <Box style={styles.dropdownHeader}>
                    <Box style={styles.dropdownHeaderTitleGroup}>
                      <Ionicons
                        name="people-outline"
                        size={15}
                        color={CCColors.Workbook.GoldText}
                      />
                      <CCText style={styles.dropdownHeaderTitle}>
                        Participants
                      </CCText>
                    </Box>
                    <Box style={styles.dropdownCountBadge}>
                      <CCText style={styles.dropdownCountText}>
                        {participantsList?.length || 0}
                      </CCText>
                    </Box>
                  </Box>
                  <CheckeredStrip height={5} />
                  <Box style={styles.participantsFlyoutBody}>
                    {renderScene({route: {key: 'PARTICIPANTS'}})}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Was the pencil icon's authoring-tools flyout - Annotations
                and Lesson now slide up from their own board-toolbar icons
                instead (see renderBoardToolbarActions), so this header slot
                is just Help now. */}
            <TouchableOpacity
              onPress={() => setShowShortcutsHelp(true)}
              style={styles.headerIconBtn}>
              <Ionicons
                name="help-circle-outline"
                size={18}
                color={CCColors.Workbook.CreamGold}
              />
            </TouchableOpacity>
          </Box>

          <Box style={styles.workbookHeaderTabsBox}>
            <WorkbookHeaderTabs
              // While a game tab is open, the workspace always shows the
              // analysis tools (see the `workbookTab === 'analysis' ||
              // activeGameTab` branch below) regardless of which pill was
              // last selected - reflect that here too, so the tab strip
              // doesn't show e.g. "Moves" as active while Analysis content
              // is what's actually on screen.
              activeTab={activeGameTab ? 'analysis' : workbookTab}
              onChangeTab={setWorkbookTab}
              isChapterLocked={isToolsTabLocked}
              showSettings={effectiveIsOwner}
            />
          </Box>
        </Box>

        <Box style={styles.body} onLayout={onParentLayout}>
          <Box
            style={[
              styles.stage,
              {
                // Equal top/bottom (was 4/0) - the shell box below now
                // centers the board+toolbar vertically within this padded
                // area on its own, so any asymmetry here would just skew
                // that centering off instead of actually doing anything.
                paddingTop: 0,
                paddingBottom: 0,
                // Split evenly with BOARD_SIDE_PADDING above, so the bigger
                // board still has a little breathing room on both sides
                // instead of sitting flush against the back button or the
                // workspace divider.
                paddingLeft: 16,
                paddingRight: 16,
                flexDirection: 'row',
                alignItems: 'stretch',
                // Centered within the stage column itself (not the whole
                // page - the workspace column still sits to its right).
                justifyContent: 'center',
              },
            ]}
            onLayout={onCenterLayout}>
            <Box
              style={{
                width: boardShellWidth,
                flexGrow: 0,
                flexShrink: 0,
                marginRight: 2,
                // Was flex-start/paddingTop 2 - pinned the board+toolbar to
                // the very top of the stage, dumping all the slack space
                // below them. Centering here (this box stretches to the
                // full stage height via the parent's alignItems: 'stretch')
                // splits that slack evenly above and below instead, so the
                // board sits a little lower rather than hugging the header.
                alignItems: 'flex-start',
                justifyContent: 'center',
              }}>
              <Box
                style={[
                  styles.chessboardContainer,
                  styles.boardStudySurface,
                  {maxWidth: maxBoardSurfaceWidth},
                ]}>
                <Box
                  style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-end',
                  }}>
                  {showEngineUi && (
                    <Box
                      style={{
                        width: 32,
                        height: boardWidth || undefined,
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginRight: 8,
                      }}>
                      {renderEvalBar()}
                    </Box>
                  )}
                  <Box
                    style={{
                      flex: 1,
                      flexDirection: 'column',
                      maxWidth: maxBoardWidth,
                      width: '100%',
                    }}>
                    <Box
                      style={{height: boardWidth || undefined}}
                      onLayout={onChessboardLayout}>
                      {renderChessboard(true)}
                    </Box>

                    {/* Lesson/Practice feedback lives in the workspace panel only —
                        showing it under the board too doubled it up and
                        pushed the toolbar down. */}

                    <Card style={styles.boardCommandBar} padding={8}>
                      {activeGameTab ? (
                        renderGameViewerTabs()
                      ) : (
                        <>
                          <NavArrows
                            forwardBlocked={isForwardNavBlockedEffective}
                            onFirstMove={handleFirstMove}
                            onPrevMove={handleToolbarPrevMove}
                            onNextMove={handleToolbarNextMove}
                            onLastMove={handleToolbarLastMove}
                            isAutoplayOn={isAutoplayOn}
                            onToggleAutoplay={() =>
                              setIsAutoplayOn(prev => !prev)
                            }
                            autoplaySpeed={autoplaySpeed}
                            onSetAutoplaySpeed={ms => {
                              setAutoplaySpeed(ms);
                              setShowAutoplayMenu(false);
                            }}
                            showSpeedMenu={showAutoplayMenu}
                            onToggleSpeedMenu={() =>
                              setShowAutoplayMenu(prev => !prev)
                            }
                          />
                          <OpeningChip openingName={openingName} large />
                          <BoardToolbarActions
                            effectiveIsOwner={effectiveIsOwner}
                            isOwner={isOwner}
                            chapterMode={selectedChapter?.chapter_mode}
                            onFlipBoard={handleFlipBoard}
                            showAnnotationsPopover={showAnnotationsPopover}
                            onToggleAnnotationsPopover={() => {
                              setShowLessonPopover(false);
                              setShowAnnotationsPopover(prev => !prev);
                            }}
                            annotationsPopover={
                              showAnnotationsPopover ? (
                                <AnnotationsPopover
                                  entered={annotationsPopoverEntered}
                                  selectedIcon={moveSelected?.annotation_icon}
                                  onSelectIcon={handleSaveAnnotationIcon}
                                />
                              ) : null
                            }
                            showLessonPopover={showLessonPopover}
                            onToggleLessonPopover={() => {
                              setShowAnnotationsPopover(false);
                              setShowLessonPopover(prev => !prev);
                            }}
                            lessonPopover={
                              showLessonPopover ? (
                                <LessonPopover
                                  entered={lessonPopoverEntered}
                                  chapterMode={selectedChapter?.chapter_mode}
                                  chapterOrientation={
                                    selectedChapter?.orientation
                                  }
                                  moveSelected={moveSelected}
                                  autoSaveStatus={lessonAutoSaveStatus}
                                  annotationDraft={annotationToShow}
                                  onChangeAnnotationDraft={setAnnotationToShow}
                                  selectedLessonRole={selectedLessonRole}
                                  isMainlineSelected={isMainlineSelected}
                                  onSetLessonRole={handleSetLessonRole}
                                  perfectFieldTab={lessonPerfectFieldTab}
                                  onSetPerfectFieldTab={
                                    setLessonPerfectFieldTab
                                  }
                                  successMessage={lessonSuccessMessage}
                                  onChangeSuccessMessage={
                                    setLessonSuccessMessage
                                  }
                                  hint={lessonHint}
                                  onChangeHint={setLessonHint}
                                  deviationMessage={lessonDeviationMessage}
                                  onChangeDeviationMessage={
                                    setLessonDeviationMessage
                                  }
                                />
                              ) : null
                            }
                            previewAsParticipant={previewAsParticipant}
                            onTogglePreview={() =>
                              setPreviewAsParticipant(!previewAsParticipant)
                            }
                          />
                        </>
                      )}
                    </Card>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box style={styles.workspace}>
            {/* One "Workbook" panel instead of two columns artificially
                labeled "Chapter"/"Worktree" that didn't describe what was
                actually inside. Its tabs (Moves, Analysis, Settings) render
                up in the page header instead (see
                renderWorkbookHeaderControls/styles.workbookHeaderTabsBox),
                so they sit on the same row as the study/chapter breadcrumb
                instead of a second row of their own; this column is just
                the tab body. */}
            <Box style={styles.workspaceColumn}>
              {workbookTab === 'settings' && !activeGameTab ? (
                effectiveIsOwner && (
                  <WorkbookSettingsTab
                    settings={studySettings}
                    studyDetails={studyDetails}
                    selectedChapter={selectedChapter}
                    moveSelected={moveSelected}
                    gameId={gameId}
                    setLoading={setLoading}
                    refetchStudy={refetchStudy}
                    setSelectedChapter={setSelectedChapter}
                  />
                )
              ) : workbookTab === 'analysis' || activeGameTab ? (
                <ScrollView
                  style={{flex: 1}}
                  showsVerticalScrollIndicator={false}>
                  {activeGameTab && (
                    // The engine/explorer tools below now analyse this
                    // game's current position (see analysisFen), so its own
                    // move list stays alongside them instead of replacing
                    // them - capped height (its own internal ScrollView
                    // handles overflow) since it's stacked inside this
                    // outer ScrollView rather than owning the column.
                    <Box style={{height: 260}}>
                      <GameMovesList
                        meta={activeGameTab.meta}
                        plies={activeGamePgnQuery.data?.plies || []}
                        currentPlyIndex={activeGameTab.plyIndex}
                        onSelectPly={jumpToGamePly}
                        isLoading={activeGamePgnQuery.isLoading}
                        isError={activeGamePgnQuery.isError}
                      />
                    </Box>
                  )}
                  {/* Sandbox sits at the top of Tools: it's the one thing here
                      you *do* rather than read. Armed, it takes over with the
                      normal practice panel; idle, it's a compact setup card.

                      Not gated on activeGameTab - that flag only widened when
                      this Tools column renders (see the workbookTab check
                      above), and neither this card nor ExerciseResultsCard
                      below existed yet when that widening was written, so
                      whether they should hide while browsing a read-only
                      explorer game (the way CevalPanel's canPlayMoves does)
                      is an open product question, not decided here. */}
                  <Box style={{padding: 12, paddingBottom: 0}}>
                    {isSandboxActive ? (
                      renderPracticePanel(false, {
                        onExit: handleExitSandbox,
                        exitLabel: 'Exit practice game',
                      })
                    ) : (
                      <PlayVsComputerCard
                        positionLabel={sandboxPositionLabel}
                        sideToMove={sandboxSideToMove}
                        onStart={handleStartSandbox}
                        blockedReason={sandboxBlockedReason}
                      />
                    )}
                  </Box>
                  {/* The coach's read side of an Exercise. Only on a practice
                      chapter, only for the owner - a participant seeing the
                      whole class's results would be a privacy leak, and the
                      endpoint refuses them anyway. */}
                  {showExerciseResults && (
                    <Box style={{padding: 12, paddingBottom: 0}}>
                      <ExerciseResultsCard
                        roster={progressRoster}
                        isLoading={isRosterLoading}
                        isError={isRosterError}
                        onRetry={refetchRoster}
                      />
                    </Box>
                  )}
                  {showStockfishOption && (
                    <Box style={{padding: 12}}>
                      <CevalPanel
                        enabled={enableStockfish}
                        // In a practice chapter the engine IS the opponent,
                        // so switching it off used to leave the student with
                        // nobody to play against and no explanation. The
                        // toggle stays available for a completed chapter
                        // being reviewed as analysis, and only refuses while
                        // an attempt is actually in progress.
                        onToggle={value => {
                          if (
                            !value &&
                            isPracticeMode &&
                            practice.outcome.status === 'ongoing' &&
                            practice.attemptMoves.length > 0
                          ) {
                            showToast(
                              'The engine is your opponent in this chapter — reset the game first if you want to turn it off.',
                              {type: 'Error'},
                            );
                            return;
                          }
                          handleCevalToggle(value);
                        }}
                        fen={sanitizeFen(analysisFen)}
                        analyzedFen={cevalFen}
                        pvs={cevalPvs}
                        depth={cevalDepth}
                        maxDepth={CEVAL_MAX_DEPTH}
                        threatMode={effectiveThreatMode}
                        threatAvailable={!!threatFen}
                        onToggleThreat={toggleThreatMode}
                        multiPv={cevalMultiPv}
                        onMultiPvChange={setCevalMultiPv}
                        showArrows={showBestMoveArrow}
                        onShowArrowsChange={setShowBestMoveArrow}
                        // Set while a practice game is running and the viewer
                        // hasn't turned assistance on. Hides this panel's PV
                        // rows and eval, and explains the arrow switch rather
                        // than leaving it reading "on" while nothing is drawn.
                        // The viewer's own arrow preference is untouched.
                        assistSuppressedReason={assistSuppressedReason}
                        onHoverPv={setHoveredPvUci}
                        onPlayPvMove={handleExplorerMoveClick}
                        canPlayMoves={
                          !activeGameTab &&
                          (!selectedChapter?.chapter_mode ||
                            selectedChapter?.chapter_mode === 'analysis')
                        }
                      />
                    </Box>
                  )}
                  {/* Explorer and tablebase are engine assistance too, and
                      carry the same "don't hand the student the answer"
                      rule as the eval bar. They used to be protected only
                      because the whole tab was locked; now that an unsolved
                      Exercise can open this tab to read why its Sandbox is
                      blocked, they need the gate stated explicitly.

                      canPlayMoves/canAddNewMoves also carry master's
                      !activeGameTab guard (added for the explorer's own
                      read-only game-tab viewer, which predates neither this
                      branch nor that one knowing about the other) - browsing
                      a historical game must stay non-interactive the same
                      way it is on the eval bar and CevalPanel above. */}
                  {!showStockfishOption && (
                    <Box style={{paddingHorizontal: 12, paddingTop: 12}}>
                      <CCText fontSize={11.5} color={CCColors.Workbook.Grey}>
                        Engine analysis, the opening explorer and the tablebase
                        unlock once you finish this exercise.
                      </CCText>
                    </Box>
                  )}
                  {showStockfishOption && (
                    <Box
                      style={{
                        paddingHorizontal: 12,
                        paddingTop: 4,
                        paddingBottom: 12,
                      }}>
                      {isTablebasePosition ? (
                        <TablebasePanel
                          data={tablebaseQuery.data}
                          isLoading={tablebaseQuery.isLoading}
                          isError={tablebaseQuery.isError}
                          sideToMoveLabel={
                            analysisFen.split(' ')[1] === 'b'
                              ? 'Black'
                              : 'White'
                          }
                          onMoveClick={handleExplorerMoveClick}
                          canPlayMoves={
                            !activeGameTab &&
                            (!selectedChapter?.chapter_mode ||
                              selectedChapter?.chapter_mode === 'analysis')
                          }
                        />
                      ) : (
                        <OpeningExplorer
                          data={explorerQuery.data}
                          isLoading={explorerQuery.isLoading}
                          isError={explorerQuery.isError}
                          error={explorerQuery.error}
                          config={explorerConfig}
                          onConfigChange={setExplorerConfig}
                          onMoveClick={handleExplorerMoveClick}
                          isMoveInTree={san => !!findExistingMoveBySan(san)}
                          canAddNewMoves={
                            !activeGameTab &&
                            (!selectedChapter?.chapter_mode ||
                              selectedChapter?.chapter_mode === 'analysis')
                          }
                          onGamePress={handleOpenGameTab}
                        />
                      )}
                    </Box>
                  )}
                </ScrollView>
              ) : selectedChapter?.chapter_mode === 'lesson' &&
                !effectiveIsOwner ? (
                <ScrollView
                  style={{flex: 1}}
                  showsVerticalScrollIndicator={false}>
                  <Box style={{padding: 20}}>
                    <Box style={{alignItems: 'center', marginVertical: 20}}>
                      <Ionicons
                        name="school-outline"
                        size={48}
                        color={CCColors.Workbook.GoldText}
                      />
                      <Spacer spacing={10} />
                      <CCText
                        fontSize={18}
                        fontWeight="bold"
                        color={CCColors.TextColor.Primary}>
                        Interactive Lesson
                      </CCText>
                      <CCText
                        fontSize={13}
                        color={CCColors.Workbook.Brown}
                        style={{textAlign: 'center', marginTop: 6}}>
                        Find the correct moves to play through this chapter.
                      </CCText>
                    </Box>

                    <Spacer spacing={10} />

                    <LessonFeedbackCard
                      positionComment={
                        (!moveSelected?.isTransientWrongAttempt &&
                          moveSelected?.annotation) ||
                        null
                      }
                      isSolved={lessonIsSolved}
                      lessonFeedback={lessonFeedback}
                      currentHint={getCorrectNextMoves()[0]?.hint}
                      hintRevealed={lessonHintRevealed}
                      hasWrongAttempt={lessonHasWrongAttempt}
                      hasPendingMoves={getCorrectNextMoves().length > 0}
                      onRevealHint={handleRevealLessonHint}
                      onViewSolution={handleViewLessonSolution}
                      onRetry={handleFirstMove}
                      hasNextChapter={hasNextChapter}
                      onNextChapter={handleGoToNextChapter}
                    />

                    <Spacer spacing={30} />
                    <TouchableOpacity
                      style={{
                        backgroundColor: isResetHovered
                          ? CCColors.Workbook.Brown
                          : CCColors.TextColor.Primary,
                        padding: 12,
                        borderRadius: 10,
                        alignItems: 'center',
                        justifyContent: 'center',
                        transitionProperty: 'all',
                        transitionDuration: '0.2s',
                      }}
                      onHoverIn={() => setIsResetHovered(true)}
                      onHoverOut={() => setIsResetHovered(false)}
                      onPress={handleFirstMove}>
                      <CCText color={CCColors.White} fontWeight="bold">
                        Reset Lesson
                      </CCText>
                    </TouchableOpacity>
                  </Box>
                </ScrollView>
              ) : isPracticeModeView ? (
                <ScrollView
                  style={{flex: 1}}
                  showsVerticalScrollIndicator={false}>
                  {renderPracticePanel(true)}
                </ScrollView>
              ) : (
                // Fixed header (search + undo strip) and fixed footer (the
                // comment box) around a bounded flex:1 middle so
                // WorkbookMovesList's own internal ScrollView is what
                // scrolls, rather than the whole column growing inside an
                // outer page-level ScrollView - the move list ends right
                // above the comment box instead of pushing it off-screen.
                <Box style={{flex: 1, minHeight: 0, flexDirection: 'column'}}>
                  {/* Search and the move-status strip are both slim,
                        fixed-height bars, so they share one row instead
                        of stacking - beside the board, not under it, so a
                        coach or student doesn't have to scroll past it to
                        see what just happened. */}
                  <Box
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginHorizontal: 12,
                      marginTop: 10,
                      marginBottom: 10,
                      gap: 8,
                    }}>
                    <Box
                      style={
                        {
                          flex: 1,
                          flexDirection: 'row',
                          alignItems: 'center',
                          height: 38,
                          borderRadius: 12,
                          borderWidth: 1,
                          borderColor: isSearchFocused
                            ? CCColors.Workbook.Brown
                            : updateColorWithOpacity(
                                CCColors.TextColor.Primary,
                                0.15,
                              ),
                          backgroundColor: CCColors.White,
                          paddingHorizontal: 12,
                          gap: 8,
                          shadowColor: CCColors.Workbook.DarkBrown,
                          shadowOffset: isSearchFocused
                            ? {width: 0, height: 3}
                            : {width: 0, height: 1},
                          shadowOpacity: isSearchFocused ? 0.08 : 0.04,
                          shadowRadius: isSearchFocused ? 6 : 3,
                          elevation: isSearchFocused ? 2 : 1,
                          transitionProperty: 'all',
                          transitionDuration: '0.2s',
                        } as any
                      }>
                      <MaterialCommunityIcons
                        name="magnify"
                        size={16}
                        color={
                          isSearchFocused
                            ? CCColors.Workbook.Brown
                            : CCColors.TextColor.Grey
                        }
                      />
                      <TextInput
                        value={moveSearchQuery}
                        onChangeText={setMoveSearchQuery}
                        onFocus={() => setIsSearchFocused(true)}
                        onBlur={() => setIsSearchFocused(false)}
                        placeholder="Search moves"
                        placeholderTextColor={CCColors.TextColor.Grey}
                        style={
                          {
                            flex: 1,
                            fontSize: 13,
                            color: CCColors.TextColor.Primary,
                            padding: 0,
                            outlineStyle: 'none',
                            borderWidth: 0,
                            height: '100%',
                            backgroundColor: 'transparent',
                          } as any
                        }
                      />
                      {moveSearchQuery.length > 0 && (
                        <TouchableOpacity
                          onPress={() => setMoveSearchQuery('')}
                          style={{
                            padding: 2,
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}>
                          <Ionicons
                            name="close-circle"
                            size={16}
                            color={CCColors.TextColor.Grey}
                          />
                        </TouchableOpacity>
                      )}
                    </Box>
                    <Box style={{flex: 1}}>
                      <MoveUpdateBar
                        undoBanner={moveUndo.undoBanner}
                        onUndo={moveUndo.undo}
                        activeMove={moveSelected}
                      />
                    </Box>
                  </Box>

                  {/* minHeight: 0 lets this flex:1 item actually shrink to
                        its share of the column instead of growing with the
                        move list's content - without it, Box's default
                        overflow:'visible' plus a flex item's default
                        min-height:auto leaves WorkbookMovesList's internal
                        ScrollView unbounded on web, so the grid doesn't fill
                        (and scroll within) the section below the search bar. */}
                  <Box style={{flex: 1, minHeight: 0}}>
                    <WorkbookMovesList
                      gameMoves={movesList?.[selectedChapter?.id as any]}
                      activeMove={moveSelected}
                      setmoveSelected={selectMoveFromList}
                      setLoading={tempLoading => setLoading(tempLoading)}
                      isOwner={isOwner}
                      highlightedMoveIds={highlightedMoveIds}
                      hideMode={isConcealMovesOn && !effectiveIsOwner}
                      moveUndo={moveUndo}
                      lessonMode={isLessonMode}
                      onNavigateStart={handleFirstMove}
                      onSetMoveRole={(move, role) =>
                        handleSaveAnnotationIcon(
                          role === 'bad' ? 'Mistake' : '',
                          move.id,
                        )
                      }
                    />
                  </Box>

                  {!effectiveIsOwner && moveSelected?.annotation ? (
                    <Box style={{marginHorizontal: 12, marginVertical: 10}}>
                      <CurrentMoveCard comment={moveSelected.annotation} />
                    </Box>
                  ) : null}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {renderConfirmAndHelpModal()}
      </Box>
      <CCLoader
        loading={
          loading || isLoading || (!!selectedChapter && isChapterMovesLoading)
        }
      />
    </Box>
  );
}

export default Study;
